import { lazy, Suspense, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Apple,
  Activity,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  BarChart3,
  Bookmark,
  Check,
  ChevronRight,
  CircleHelp,
  Copy,
  ExternalLink,
  Flag,
  FolderOpen,
  Globe2,
  History,
  Info,
  Link2,
  LogIn,
  LogOut,
  Menu,
  Plus,
  RotateCcw,
  Search,
  Share2,
  Sparkles,
  Sun,
  Moon,
  SunMoon,
  Trash2,
  TrendingUp,
  UserRound,
  X,
} from "lucide-react";
import { invalidateAuthSession, refreshAuthSession, useAuthSession } from "./lib/authSession";
import { loadAuthClient } from "./lib/authClientLoader";
import { runSignOut } from "./lib/authSignOut";
import { PaginationControls } from "./components/PaginationControls";
import { PageHero, PageLayout } from "./components/PageLayout";
import { AdminLink } from "./components/AdminLink";
import { findBlogPostMeta as findBlogPost } from "./lib/blogMeta";
import { faqStructuredData, homeFaqItems } from "./lib/faqData";
import {
  colors,
  findPortfolio,
  holdingsWithTone,
  portfolioIdeas,
  rangeLabels,
  type Holding,
  type PortfolioIdea,
  type Range,
} from "./lib/portfolioData";
import { loadAccountPortfolios, loadPortfolioEstimate, loadPublicPortfolios, loadSavedPortfolio, savedPortfolioRoute, type PortfolioEstimate, type PublicPortfolioPayload, type SavedPortfolioPayload } from "./lib/portfolioApi";
import { BillingApiError, loadAccountPlan, openBillingPortal, startBillingCheckout, type AccountPlan } from "./lib/billingApi";
import { createReplayCardPortraitSvg, createReplayCardSvg } from "./lib/portfolioCard";
import { shareReplayCard } from "./lib/shareCard";
import { usePreparedReplayCard } from "./hooks/usePreparedReplayCard";
import { loadPortfolioMiniChart } from "./lib/stockApi";
import { findStock } from "./lib/stockData";
import { searchRows, type SearchResult } from "./lib/stockSearch";
import { calculatorCatalog, featuredCalculatorSlugs, findCalculator } from "./lib/calculatorData";
import { dateFormatter, displayDate, money, percent, safeNumber, validStartingValue } from "./lib/formatters";
import { trackPageView } from "./lib/analytics";
import { navigate } from "./lib/navigation";
import { escapeRegExp } from "./lib/routeUtils";
import { getStockMarketQuizSeoMetadata } from "./lib/stockMarketQuizSeo";
import { discoveryCopy } from "./lib/discoveryCopy";
import { appPaths, buildPortfolioRoute, stockMarketQuizRoute } from "./routes.config";

const stockDetailRoutePattern = new RegExp(`^${escapeRegExp(appPaths.stocks)}/([A-Za-z0-9.-]+)/?$`);
const calculatorRoutePattern = new RegExp(`^${escapeRegExp(appPaths.calculators)}(?:/([a-z0-9-]+))?/?$`);
const PRODUCT_HUNT_URL = "https://www.producthunt.com/products/equityreplay";

const LazyLegalPage = lazy(() => import("./legal").then(({ LegalPage }) => ({ default: LegalPage })));
const BlogIndexPage = lazy(() => import("./routes/BlogPages").then(({ BlogIndexPage }) => ({ default: BlogIndexPage })));
const BlogPostPage = lazy(() => import("./routes/BlogPages").then(({ BlogPostPage }) => ({ default: BlogPostPage })));
const LazyFaqPage = lazy(() => import("./faq").then(({ FaqPage }) => ({ default: FaqPage })));
const LazyMethodologyPage = lazy(() => import("./methodology").then(({ MethodologyPage }) => ({ default: MethodologyPage })));
const LazyModerationPage = lazy(() => import("./ModerationPage").then(({ ModerationPage }) => ({ default: ModerationPage })));
const LazyAdminPage = lazy(() => import("./AdminPage").then(({ AdminPage }) => ({ default: AdminPage })));
const LazyCalculatorsPage = lazy(() => import("./calculators").then(({ CalculatorsPage }) => ({ default: CalculatorsPage })));
const LazyContactPage = lazy(() => import("./contact").then(({ ContactPage }) => ({ default: ContactPage })));
const LazyStockExplorerPage = lazy(() => import("./routes/MarketPages").then(({ StockExplorerPage }) => ({ default: StockExplorerPage })));
const LazyStockDetailPage = lazy(() => import("./routes/MarketPages").then(({ StockDetailPage }) => ({ default: StockDetailPage })));
const LazyStockComparisonPage = lazy(() => import("./routes/MarketPages").then(({ StockComparisonPage }) => ({ default: StockComparisonPage })));
const LazyStockMarketQuizPage = lazy(() => import("./stockMarketQuiz").then(({ StockMarketQuizPage }) => ({ default: StockMarketQuizPage })));

type Estimate = PortfolioEstimate;

type PortfolioRoute = {
  name: string;
  description: string;
  tag: string;
  slug: string;
  holdings: Holding[];
  range: Range;
  sourceLabel?: string;
  sourceUrl?: string;
  asOf?: string;
  disclosureNote?: string;
  aiNotes?: Array<{ symbol: string; reason: string }>;
  startingValue?: number;
  visibility?: "private" | "public";
  canEdit?: boolean;
  replayPath?: string;
};

type AiMixMetadata = {
  summary: string;
  notes: Array<{ symbol: string; reason: string }>;
};

type AiMixResponse = {
  name: string;
  summary: string;
  notice?: string;
  holdings: Array<{ symbol: string; name: string; weight: number; reason: string }>;
};

type AiMixError = { error?: string; code?: string; symbols?: string[]; limit?: number; upgradePath?: string | null };
type AiMixRange = Extract<Range, "1y" | "3y" | "5y">;

type ThemePreference = "system" | "light" | "dark";

const investorIdeas = portfolioIdeas.filter((idea) => idea.category === "investor");
const heroIdea = findPortfolio("warren-buffett-berkshire-core") ?? investorIdeas[0]!;
const heroPortfolioSlugs = [
  "warren-buffett-berkshire-core",
  "donald-trump-public-disclosure",
  "ray-dalio-bridgewater-snapshot",
] as const;
const heroIdeas = heroPortfolioSlugs.flatMap((slug) => {
  const idea = findPortfolio(slug);
  return idea ? [idea] : [];
});
const heroCardTitles: Record<string, string> = {
  "warren-buffett-berkshire-core": "Warren Buffett disclosure",
  "donald-trump-public-disclosure": "Donald Trump disclosure",
  "ray-dalio-bridgewater-snapshot": "Ray Dalio disclosure",
};
const heroEstimateCache = new Map<string, Promise<Estimate>>();
const HERO_ROTATION_INTERVAL_MS = 5000;

function loadHeroEstimate(idea: PortfolioIdea) {
  const cached = heroEstimateCache.get(idea.slug);
  if (cached) return cached;
  const request = loadPortfolioEstimate({ holdings: idea.holdings.map((holding) => ({ symbol: holding.symbol, weight: holding.weight })), range: "10y", startingValue: 10_000 }).catch((error) => {
    heroEstimateCache.delete(idea.slug);
    throw error;
  });
  heroEstimateCache.set(idea.slug, request);
  return request;
}
const quickStartSlugs = [
  "warren-buffett-berkshire-core",
  "donald-trump-public-disclosure",
  "mega-cap-compounders",
  "ai-infrastructure",
];
const quickStartIdeas: PortfolioIdea[] = [];
for (const slug of quickStartSlugs) {
  const idea = findPortfolio(slug);
  if (idea) quickStartIdeas.push(idea);
}
const footerInvestorSlugs = new Set([
  "warren-buffett-berkshire-core",
  "donald-trump-public-disclosure",
  "ray-dalio-bridgewater-snapshot",
  "bill-ackman-pershing-square-snapshot",
  "bill-gates-gates-foundation-snapshot",
]);
const publicInvestorLinks: Array<{ label: string; slug: string }> = [];
for (const idea of investorIdeas) {
  if (footerInvestorSlugs.has(idea.slug)) publicInvestorLinks.push({ label: idea.label.split(" / ")[0].replace(/ public disclosure$/i, ""), slug: idea.slug });
}
const portfolioPageSize = 10;
const stockPageSize = 10;

function DeferredPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="route-loading" aria-live="polite">Loading page…</div>}>{children}</Suspense>;
}

function readThemePreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem("equity-replay-theme");
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  } catch {
    return "system";
  }
}

function themeLabel(theme: ThemePreference) {
  return theme === "light" ? "Light" : theme === "dark" ? "Dark" : "System";
}

function ThemeControl({ theme, onChange }: { theme: ThemePreference; onChange: (theme: ThemePreference) => void }) {
  const Icon = theme === "light" ? Sun : theme === "dark" ? Moon : SunMoon;
  const nextTheme = theme === "system" ? "light" : theme === "light" ? "dark" : "system";
  return <button className="theme-control" type="button" onClick={() => onChange(nextTheme)} aria-label={`Theme: ${themeLabel(theme)}. Switch to ${themeLabel(nextTheme)} theme`} title={`Theme: ${themeLabel(theme)} · Switch to ${themeLabel(nextTheme)}`}><Icon size={16} aria-hidden="true" /><span className="sr-only">{themeLabel(theme)} theme</span></button>;
}

function accountDate(value?: number) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
}

async function readJson<T>(response: Response): Promise<T | null> {
  return response.json<T>().catch(() => null);
}

function openIdeaRoute(idea: PortfolioIdea) {
  navigate(`/portfolio/${idea.slug}`);
}

function openStockRoute(symbol: string) {
  navigate(`${appPaths.stocks}/${encodeURIComponent(symbol)}`);
}

function safeInternalHref(value: string | undefined) {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  try {
    const url = new URL(value, "https://equityreplay.invalid");
    if (url.origin !== "https://equityreplay.invalid") return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

function safeExternalHref(value: string | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function startCustomRoute(holdings: Holding[], name: string, range: Range, aiMix?: AiMixMetadata) {
  const params = new URLSearchParams({ name, range, holdings: JSON.stringify(holdings.map(({ symbol, name: company, weight }) => ({ symbol, name: company, weight }))) });
  if (aiMix) {
    params.set("source", "ai");
    params.set("summary", aiMix.summary);
    params.set("notes", JSON.stringify(aiMix.notes));
  }
  navigate(`/portfolio/custom?${params.toString()}`);
}

function goHomeRoute() {
  navigate(appPaths.home);
}

function updatePageMetadataImpl(title: string, description: string, path: string | null, structuredData?: Record<string, unknown>, robots = "index,follow", ogType = "website", analyticsPath?: string, navigationPath?: string) {
  const resolvedNavigationPath = navigationPath ?? analyticsPath ?? path ?? (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/");
  const resolvedAnalyticsPath = analyticsPath ?? path ?? (resolvedNavigationPath.split(/[?#]/, 1)[0] || "/");
  if (typeof document !== "undefined") {
    document.title = title;
    const descriptionMeta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (descriptionMeta) descriptionMeta.content = description;
    for (const [property, content] of [["og:title", title], ["og:description", description], ["og:type", ogType], ["twitter:title", title], ["twitter:description", description]] as const) {
      const meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"], meta[name="${property}"]`);
      if (meta) meta.content = content;
    }
    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (path) {
      const canonicalUrl = `${window.location.origin}${path}`;
      if (ogUrl) ogUrl.content = canonicalUrl;
      if (canonical) canonical.href = canonicalUrl;
      else {
        const link = document.createElement("link");
        link.rel = "canonical";
        link.href = canonicalUrl;
        document.head.appendChild(link);
      }
    } else {
      ogUrl?.remove();
      canonical?.remove();
    }
    const robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (robotsMeta) robotsMeta.content = robots;
    let structured = document.querySelector<HTMLScriptElement>("#structured-data");
    if (structuredData) {
      if (!structured) {
        structured = document.createElement("script");
        structured.id = "structured-data";
        structured.type = "application/ld+json";
        document.head.appendChild(structured);
      }
      structured.textContent = JSON.stringify(structuredData).replace(/</g, "\\u003c");
    } else {
      structured?.remove();
    }
  }
  trackPageView(resolvedAnalyticsPath, title, resolvedNavigationPath);
}

function requestAuth() {
  window.dispatchEvent(new CustomEvent("portfolio:auth-required"));
}

async function signOutAndLeave() {
  await runSignOut(() => {
    invalidateAuthSession();
    navigate(appPaths.home);
  });
}

function LogoContent() {
  return <><span className="logo-mark" aria-hidden="true"><svg viewBox="0 0 36 36" role="presentation"><path d="m8 25 6.4-6.4 5 3.4L28 12" /><path d="M23.5 12H28v4.5" /></svg></span><span className="logo-wordmark">equity<span className="logo-dot">.</span>replay</span></>;
}

function Logo({ onClick }: { onClick?: () => void }) {
  return onClick ? <a className="logo" href={appPaths.home} onClick={(event) => { if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onClick(); } }} aria-label="equity.replay home"><LogoContent /></a> : <div className="logo"><LogoContent /></div>;
}

function ProductHuntBadge() {
  return <a className="product-hunt-badge" href={PRODUCT_HUNT_URL} target="_blank" rel="noreferrer noopener" aria-label="Featured on Product Hunt, open EquityReplay on Product Hunt">
    <span className="product-hunt-badge-mark" aria-hidden="true">P</span>
    <span className="product-hunt-badge-copy"><span>Featured on</span><strong>Product Hunt</strong></span>
    <ExternalLink size={13} aria-hidden="true" />
  </a>;
}

function Header({ onHome }: { onHome: () => void }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  return (
    <header className="site-header">
      <div className="header-inner shell">
        <Logo onClick={onHome} />
        <nav className="primary-nav" aria-label="Primary navigation">
          <a href={appPaths.stocks}>Stocks</a>
          <a href={appPaths.explore}>Portfolios</a>
          <a href={appPaths.comparePortfolios}>Compare</a>
          <a href={appPaths.pricing}>Pricing</a>
        </nav>
        <div className="header-actions"><AuthControl /><a className="header-cta" href={appPaths.buildPortfolio}>Build a portfolio <ArrowRight size={15} /></a><button className="mobile-nav-toggle" type="button" onClick={() => setMobileNavOpen(!mobileNavOpen)} aria-expanded={mobileNavOpen} aria-controls="mobile-navigation" aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}>{mobileNavOpen ? <X size={17} /> : <Menu size={17} />}</button></div>
      </div>
      <nav id="mobile-navigation" className={`mobile-nav ${mobileNavOpen ? "open" : ""}`} aria-label="Mobile navigation"><a href={appPaths.stocks}>Stocks</a><a href={appPaths.explore}>Portfolios</a><a href={appPaths.comparePortfolios}>Compare</a><a href={appPaths.pricing}>Pricing</a><a href={appPaths.buildPortfolio}>Build a portfolio <ArrowRight size={15} /></a></nav>
    </header>
  );
}

function AuthControl() {
  const { data: session, isPending, ensureSession } = useAuthSession();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<"google" | "apple" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const openAuth = () => { setOpen(true); setError(null); };
    window.addEventListener("portfolio:auth-required", openAuth);
    return () => window.removeEventListener("portfolio:auth-required", openAuth);
  }, []);

  async function signIn(provider: "google" | "apple") {
    setBusy(provider);
    setError(null);
    try {
      const authClient = await loadAuthClient();
      const result = await authClient.signIn.social({ provider, callbackURL: window.location.pathname + window.location.search });
      if (result.error) setError(result.error.message ?? "Sign-in is not configured yet.");
      else void refreshAuthSession();
    } catch {
      setError("Sign-in is not configured yet. Add the provider credentials to enable it.");
    } finally {
      setBusy(null);
    }
  }

  async function signOut() {
    await runSignOut(() => {
      invalidateAuthSession();
      setOpen(false);
      navigate(appPaths.home);
    });
  }

  if (isPending && !open) return <span className="auth-loading">Account</span>;
  if (session?.user) {
  return <div className="auth-control"><button className="account-button" onClick={() => setOpen(!open)} aria-expanded={open}><span className="account-avatar">{session.user.name?.slice(0, 1).toUpperCase() ?? "A"}</span><span className="account-name">{session.user.name?.split(" ")[0] ?? "Account"}</span></button>{open && <div className="auth-popover signed-in"><span className="auth-popover-label">Your account</span><strong>{session.user.name}</strong><small>{session.user.email}</small><a className="auth-account-link" href={appPaths.portfolios}><FolderOpen size={14} aria-hidden="true" /><span>Saved portfolios</span><ArrowRight size={14} aria-hidden="true" /></a><a className="auth-account-link" href={appPaths.account}><UserRound size={14} aria-hidden="true" /><span>Account settings</span><ArrowRight size={14} aria-hidden="true" /></a><button onClick={() => void signOut()}><LogOut size={14} /> Sign out</button></div>}</div>;
  }
  return <div className="auth-control"><button className="account-button sign-in-button" onClick={() => { const nextOpen = !open; setOpen(nextOpen); if (nextOpen) void ensureSession(); }} aria-expanded={open}><LogIn size={15} /> Sign in</button>{open && <div className="auth-popover"><span className="auth-popover-label">Use your account</span><strong>Keep your portfolios close.</strong><p>Sign in to create AI mixes, save portfolios, and return to them later.</p><button onClick={() => void signIn("google")} disabled={Boolean(busy)}><span className="provider-mark google-mark">G</span>{busy === "google" ? "Connecting…" : "Continue with Google"}</button><button onClick={() => void signIn("apple")} disabled={Boolean(busy)}><Apple size={16} />{busy === "apple" ? "Connecting…" : "Continue with Apple"}</button>{error && <span className="auth-error" role="alert">{error}</span>}</div>}</div>;
}

function LoginPage() {
  const { data: session, isPending } = useAuthSession({ eager: true });
  const [busy, setBusy] = useState<"google" | "apple" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session?.user) navigate(appPaths.account);
  }, [session?.user]);

  async function signIn(provider: "google" | "apple") {
    setBusy(provider);
    setError(null);
    try {
      const authClient = await loadAuthClient();
      const result = await authClient.signIn.social({ provider, callbackURL: appPaths.account });
      if (result.error) setError(result.error.message ?? "Sign-in is not available right now.");
    } catch {
      setError("Sign-in is not available right now. Please try again.");
    } finally {
      setBusy(null);
    }
  }

  if (isPending) return <PageLayout className="auth-page"><div className="auth-page-card"><History size={22} /><p>Checking your account…</p></div></PageLayout>;
  return <PageLayout className="auth-page"><div className="auth-page-card"><span className="section-label"><UserRound size={13} /> Account access</span><h1>Keep your portfolios close.</h1><p>Sign in to create AI mixes, save your allocations, and return to them whenever you want.</p><div className="auth-page-actions"><button className="primary-button" onClick={() => void signIn("google")} disabled={Boolean(busy)}><span className="provider-mark google-mark">G</span>{busy === "google" ? "Connecting…" : "Continue with Google"}</button><button className="secondary-button" onClick={() => void signIn("apple")} disabled={Boolean(busy)}><Apple size={16} />{busy === "apple" ? "Connecting…" : "Continue with Apple"}</button></div>{error && <p className="auth-page-error" role="alert">{error}</p>}<small>We use your provider account only to manage your Equity Replay profile and saved portfolios.</small></div></PageLayout>;
}

function AccountPortfolioCard({ portfolio }: { portfolio: SavedPortfolioPayload }) {
  const holdings = Array.isArray(portfolio.holdings) ? portfolio.holdings : [];
  const portfolioHref = `/portfolio/saved/${encodeURIComponent(portfolio.slug)}`;
  return <article className="account-portfolio-card"><div className="account-portfolio-card-top"><span className="account-portfolio-status">{portfolio.visibility === "public" ? "Published" : "Private"}</span><span>{accountDate(portfolio.updatedAt ?? portfolio.createdAt)}</span></div><a href={portfolioHref}><h3>{portfolio.name}</h3><PortfolioMiniChart holdings={holdings} /><div className="account-portfolio-meta"><span>{holdings.length} positions</span><span>{rangeLabels[portfolio.range as Range] ?? portfolio.range}</span>{portfolio.visibility === "public" && <span>{portfolio.viewCount ?? 0} views</span>}</div><AllocationStrip holdings={holdings} /></a></article>;
}

function SignedOutAccountPage({ title, description }: { title: string; description: string }) {
  return <PageLayout className="account-page"><PageHero as="div" className="account-empty-account"><span className="section-label"><UserRound size={13} /> Account</span><h1>{title}</h1><p>{description}</p><a className="primary-button" href={appPaths.login}>Sign in <ArrowRight size={15} /></a></PageHero></PageLayout>;
}

function PortfoliosPage() {
  const { data: session, isPending } = useAuthSession({ eager: true });
  const [portfolios, setPortfolios] = useState<SavedPortfolioPayload[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    void loadAccountPortfolios()
      .then((items) => { if (active) setPortfolios(items); })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : "Your portfolios could not be loaded."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [session?.user]);

  if (isPending) return <PageLayout className="account-page"><div className="auth-page-card"><History size={22} /><p>Loading your account…</p></div></PageLayout>;
  if (!session?.user) return <SignedOutAccountPage title="Sign in to see your portfolios." description="Your saved allocations and published portfolios will live here." />;

  const pageCount = Math.max(1, Math.ceil(portfolios.length / portfolioPageSize));
  const safePage = Math.min(page, pageCount);
  const visiblePortfolios = portfolios.slice((safePage - 1) * portfolioPageSize, safePage * portfolioPageSize);
  return <PageLayout className="account-page"><PageHero as="header" className="account-page-header"><div><span className="section-label"><FolderOpen size={13} /> Portfolios</span><h1>Your portfolio library.</h1><p>Open, review, and manage the portfolios you’ve saved.</p></div><div className="account-page-header-actions"><span className="account-count">{portfolios.length} saved {portfolios.length === 1 ? "portfolio" : "portfolios"}</span><a className="secondary-button" href={appPaths.buildPortfolio}>Build a portfolio <ArrowRight size={15} /></a></div></PageHero><section className="account-section" aria-label="Saved portfolios">{loading ? <div className="account-state"><History size={20} /><p>Loading your portfolios…</p></div> : error ? <div className="account-state account-state-error"><Info size={20} /><p>{error}</p></div> : portfolios.length === 0 ? <div className="account-state"><FolderOpen size={20} /><h3>Nothing saved yet.</h3><p>Build a mix, then save it here for later.</p><a className="primary-button" href={appPaths.buildPortfolio}>Build your first portfolio <ArrowRight size={15} /></a></div> : <><div className="account-portfolio-grid">{visiblePortfolios.map((portfolio) => <AccountPortfolioCard key={portfolio.slug} portfolio={portfolio} />)}</div><PaginationControls page={safePage} pageCount={pageCount} total={portfolios.length} pageSize={portfolioPageSize} label="portfolios" onPageChange={setPage} /></>}</section></PageLayout>;
}

function PricingPage() {
  const { data: session, ensureSession } = useAuthSession();
  const [plan, setPlan] = useState<AccountPlan | null>(null);
  const [busy, setBusy] = useState<"monthly" | "annual" | "portal" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user) {
      setPlan(null);
      return;
    }
    let active = true;
    void loadAccountPlan().then((value) => { if (active) setPlan(value); }).catch(() => { if (active) setPlan(null); });
    return () => { active = false; };
  }, [session?.user]);

  async function upgrade(interval: "monthly" | "annual") {
    const currentSession = session ?? await ensureSession();
    if (!currentSession?.user) {
      requestAuth();
      setError("Sign in first, then choose Replay Pro for this account.");
      return;
    }
    setBusy(interval);
    setError(null);
    try {
      const checkout = await startBillingCheckout(interval);
      window.location.assign(checkout.url);
    } catch (reason) {
      setError(reason instanceof BillingApiError && reason.code === "BILLING_NOT_CONFIGURED" ? "Replay Pro checkout is being connected. Try again soon." : reason instanceof Error ? reason.message : "Replay Pro checkout is unavailable right now.");
    } finally {
      setBusy(null);
    }
  }

  async function manageBilling() {
    setBusy("portal");
    setError(null);
    try {
      const portal = await openBillingPortal();
      window.location.assign(portal.url);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Billing management is unavailable right now.");
    } finally {
      setBusy(null);
    }
  }

  return <PageLayout className="pricing-page"><PageHero as="header" className="pricing-header"><span className="section-label"><Sparkles size={13} /> Replay Pro</span><h1>More room for the mixes you keep.</h1><p>Keep the simulator, historical windows, public portfolios, charts, and sharing free. Replay Pro simply gives you more saved portfolios and more AI drafts each month.</p></PageHero><div className="pricing-grid"><article className="pricing-card"><div><span className="section-label">Free</span><h2>Start exploring.</h2><p>Everything needed to build and replay portfolios without a subscription.</p></div><ul><li><Check size={14} /> Unlimited stock exploration</li><li><Check size={14} /> All historical windows</li><li><Check size={14} /> 5 saved portfolios</li><li><Check size={14} /> 3 AI mixes each month</li><li><Check size={14} /> Public pages and standard share cards</li></ul><span className="pricing-current">{plan && !plan.isPro ? "Your current plan" : "No card required"}</span></article><article className="pricing-card pricing-card-pro"><div><span className="section-label"><Sparkles size={13} /> Replay Pro</span><h2>$5.99<span>/month</span></h2><p>For people who want to keep more versions and use AI regularly.</p></div><ul><li><Check size={14} /> 50 saved portfolios</li><li><Check size={14} /> 50 AI mixes each month</li><li><Check size={14} /> Save and revisit AI-created mixes</li><li><Check size={14} /> Higher fair-use limits</li><li><Check size={14} /> Cancel from the billing portal</li></ul>{plan?.isPro ? <button className="primary-button" onClick={() => void manageBilling()} disabled={busy === "portal"}>{busy === "portal" ? "Opening billing…" : "Manage Replay Pro"} <ArrowRight size={15} /></button> : <div className="pricing-actions"><button className="primary-button" onClick={() => void upgrade("monthly")} disabled={Boolean(busy)}>{busy === "monthly" ? "Opening checkout…" : "Choose monthly"} <ArrowRight size={15} /></button><button className="secondary-button" onClick={() => void upgrade("annual")} disabled={Boolean(busy)}>{busy === "annual" ? "Opening checkout…" : "$49 yearly"}</button></div>}</article></div>{error && <p className="pricing-error" role="alert"><Info size={14} /> {error}</p>}<p className="pricing-footnote">Replay Pro changes access to saved-portfolio and AI-draft limits only. It does not provide trades, recommendations, predictions, or financial advice.</p></PageLayout>;
}

function AccountPage() {
  const { data: session, isPending } = useAuthSession({ eager: true });
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [plan, setPlan] = useState<AccountPlan | null>(null);
  const [billingBusy, setBillingBusy] = useState(false);

  useEffect(() => {
    if (!session?.user) {
      setPlan(null);
      return;
    }
    let active = true;
    void loadAccountPlan().then((value) => { if (active) setPlan(value); }).catch(() => { if (active) setPlan(null); });
    return () => { active = false; };
  }, [session?.user]);

  async function manageBilling() {
    if (!plan?.isPro) {
      navigate(appPaths.pricing);
      return;
    }
    setBillingBusy(true);
    try {
      const portal = await openBillingPortal();
      window.location.assign(portal.url);
    } catch {
      setDeleteError("Billing management is unavailable right now. Please try again shortly.");
    } finally {
      setBillingBusy(false);
    }
  }

  async function deleteAccount() {
    if (!window.confirm("Delete your account and all saved portfolios? This cannot be undone.")) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const authClient = await loadAuthClient();
      const result = await authClient.deleteUser();
      if (result.error) throw new Error(result.error.message ?? "Your account could not be deleted.");
      invalidateAuthSession();
      navigate(appPaths.home);
    } catch (reason) {
      setDeleteError(reason instanceof Error ? reason.message : "Your account could not be deleted. Please sign in again and retry.");
    } finally {
      setDeleting(false);
    }
  }

  if (isPending) return <PageLayout className="account-page"><div className="auth-page-card"><History size={22} /><p>Loading your account…</p></div></PageLayout>;
  if (!session?.user) return <SignedOutAccountPage title="Sign in to manage your account." description="Your profile and account controls will live here." />;

  const savedLimit = plan?.limits.savedPortfolios ?? 5;
  const aiLimit = plan?.limits.aiMixesPerMonth ?? 3;
  return <PageLayout className="account-page"><div className="account-breadcrumb"><a href={appPaths.home}>Home</a><span>/</span><strong>Account</strong></div><PageHero as="header" className="account-compact-header"><span className="section-label"><UserRound size={13} /> Account</span><h1>Account settings</h1><p>Manage your profile, plan, saved portfolios, and account data in one place.</p></PageHero><div className="account-settings-layout"><div className="account-settings-content"><section className="account-profile-overview" id="profile" aria-labelledby="account-profile-name"><div className="account-profile-identity"><span className="account-avatar account-avatar-large">{session.user.name?.slice(0, 1).toUpperCase() ?? "A"}</span><div><span className="section-label">Signed in as</span><h2 id="account-profile-name">{session.user.name}</h2><p>{session.user.email}</p><span className="account-profile-note">Your Google or Apple sign-in manages access to this account.</span></div></div><div className="account-profile-actions"><AdminLink userId={session.user.id} /><a className="secondary-button" href={appPaths.portfolios}><FolderOpen size={14} /> Saved portfolios <ArrowRight size={14} /></a><button className="secondary-button" onClick={() => void signOutAndLeave()}><LogOut size={14} /> Sign out</button></div></section><section className="account-plan-card" aria-labelledby="account-plan-heading"><div><span className="section-label"><Sparkles size={13} /> Plan</span><h2 id="account-plan-heading">{plan?.planName ?? "Free"}</h2><p>{plan?.isPro ? "You have more room for saved portfolios and AI drafts." : "The core simulator is free. Upgrade when you need more saved portfolios or AI drafts."}</p></div><div className="account-plan-usage"><div><strong>{plan?.usage.savedPortfolios ?? 0}</strong><span>of {savedLimit} saved</span></div><div><strong>{plan?.usage.aiMixesThisMonth ?? 0}</strong><span>of {aiLimit} AI this month</span></div></div><button className="secondary-button" onClick={() => void manageBilling()} disabled={billingBusy}>{billingBusy ? "Opening billing…" : plan?.isPro ? "Manage billing" : "View Replay Pro"} <ArrowRight size={14} /></button></section><section className="account-danger" id="danger-zone"><div><span className="section-label"><Trash2 size={13} /> Danger zone</span><h2>Delete account</h2><p>Permanently removes your profile, saved portfolios, AI usage history, and published portfolio records.</p></div><div><button className="danger-button" onClick={() => void deleteAccount()} disabled={deleting}><Trash2 size={14} />{deleting ? "Deleting…" : "Delete my account"}</button>{deleteError && <p className="account-error" role="alert">{deleteError}</p>}</div></section></div></div></PageLayout>;
}

function AllocationStrip({ holdings }: { holdings: Array<{ symbol: string; weight: number }> }) {
  return (
    <div className="allocation-strip" aria-label="Portfolio allocation">
      {holdings.map((holding, index) => (
        <span
          key={holding.symbol}
          style={{ width: `${holding.weight}%`, background: colors[index % colors.length] }}
          title={`${holding.symbol} ${holding.weight}%`}
        />
      ))}
    </div>
  );
}

function Chart({ points, benchmark, positive, startingValue, label = "Portfolio estimate", valueFormatter = money }: { points: Estimate["points"]; benchmark: Estimate["benchmark"]; positive: boolean; startingValue: number; label?: string; valueFormatter?: (value: number) => string }) {
  const gradientId = useId().replace(/:/g, "");
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const width = 900;
  const height = 330;
  if (!points.length) return <div className="chart-empty">Run the estimate to draw the historical view.</div>;
  const allIndexes = [...points, ...(benchmark?.points ?? [])].map((point) => point.index);
  const min = Math.min(...allIndexes);
  const max = Math.max(...allIndexes);
  const spread = Math.max(1, max - min);
  const pathFor = (series: Estimate["points"]) => series.map((point, index) => {
    const x = (index / Math.max(1, points.length - 1)) * width;
    const y = height - ((point.index - min + spread * 0.08) / (spread * 1.16)) * height;
    return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  const path = pathFor(points);
  const benchmarkPath = benchmark ? pathFor(benchmark.points) : null;
  const area = `${path} L${width},${height} L0,${height} Z`;
  const stroke = positive ? "var(--color-accent)" : "var(--color-negative)";
  const hoverPoint = hoverIndex === null ? null : points[hoverIndex];
  return (
    <div className="chart-wrap" onMouseLeave={() => setHoverIndex(null)}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={benchmark ? `${label} historical chart with an SPY reference line` : `${label} historical chart`} preserveAspectRatio="none" onMouseMove={(event) => {
        const bounds = event.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
        setHoverIndex(Math.round(ratio * (points.length - 1)));
      }} tabIndex={0} onFocus={() => setHoverIndex(0)} onBlur={() => setHoverIndex(null)} onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight" && event.key !== "Home" && event.key !== "End") return;
        event.preventDefault();
        setHoverIndex((current) => {
          const index = current ?? 0;
          if (event.key === "Home") return 0;
          if (event.key === "End") return points.length - 1;
          return Math.min(points.length - 1, Math.max(0, index + (event.key === "ArrowRight" ? 1 : -1)));
        });
      }}>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={stroke} stopOpacity=".24" />
            <stop offset="1" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0 82.5 H900 M0 165 H900 M0 247.5 H900" className="chart-grid" />
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={path} fill="none" stroke={stroke} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {benchmarkPath && <path d={benchmarkPath} fill="none" stroke="var(--color-ink-3)" strokeWidth="1.5" strokeDasharray="7 7" strokeLinecap="round" strokeLinejoin="round" />}
        {hoverPoint && <line x1={(hoverIndex! / Math.max(1, points.length - 1)) * width} x2={(hoverIndex! / Math.max(1, points.length - 1)) * width} y1="0" y2={height} className="chart-hover-line" />}
      </svg>
      {hoverPoint && <div className="chart-tooltip" style={{ left: `${(hoverIndex! / Math.max(1, points.length - 1)) * 100}%` }}><b>{displayDate(hoverPoint.date)}</b><span>{valueFormatter(startingValue * hoverPoint.index / 100)}</span><small>{percent(hoverPoint.index - 100)} from start</small>{benchmark?.points[hoverIndex!] && <small>{benchmark.symbol} {percent(benchmark.points[hoverIndex!].index - 100)}</small>}</div>}
      <div className="chart-legend"><span className="chart-legend-portfolio"><i /> {label}</span>{benchmark && <span className="chart-legend-benchmark"><i /> {benchmark.label} · {percent(benchmark.returnPct)}</span>}</div>
      <div className="chart-axis"><span>{displayDate(points[0]?.date)}</span><span>{displayDate(points[Math.floor(points.length / 2)]?.date)}</span><span>{displayDate(points.at(-1)?.date)}</span></div>
    </div>
  );
}

type HeroPreviewStatus = "loading" | "ready" | "error";

function HeroPreviewChart({ estimate, status }: { estimate: Estimate | null; status: HeroPreviewStatus }) {
  const gradientId = `hero-preview-gradient-${useId().replace(/:/g, "")}`;
  if (estimate) {
    const path = replayPreviewPath(estimate.points);
    const area = `${path} L 100 44 L 0 44 Z`;
    const positive = estimate.returnPct >= 0;
    const chartColor = positive ? "var(--color-accent)" : "var(--color-negative)";
    return <div className="preview-chart" role="img" aria-label={`Ten-year historical replay chart ending at ${money(estimate.endingValue)} with a ${percent(estimate.returnPct)} change`}>
      <svg viewBox="0 0 100 44" preserveAspectRatio="none" aria-hidden="true">
        <defs><linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={chartColor} stopOpacity=".24" /><stop offset="1" stopColor={chartColor} stopOpacity="0" /></linearGradient></defs>
        <path d="M0 11 H100 M0 22 H100 M0 33 H100" className="preview-chart-grid" />
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={path} className={`preview-chart-line ${positive ? "positive" : "negative"}`} />
      </svg>
    </div>;
  }

  const resultState = status === "loading" ? "Loading the replay…" : "Replay unavailable right now";
  const resultHint = status === "loading" ? "Fetching ten years of close-price history" : "Open the portfolio to try the historical replay";
  return <div className={`preview-chart-state ${status}`} role="status"><span className="preview-chart-pulse" /><strong>{resultState}</strong><small>{resultHint}</small></div>;
}

function HeroPreviewMetrics({ estimate }: { estimate: Estimate | null }) {
  const positive = (estimate?.returnPct ?? 0) >= 0;
  const estimatedValue = estimate ? money(estimate.endingValue) : "—";
  const change = estimate ? percent(estimate.returnPct) : "—";
  const changeTone = estimate ? (positive ? "positive" : "negative") : "";
  return <div className="preview-metrics">
    <div><span>Estimated value</span><strong>{estimatedValue}</strong></div>
    <div><span>Historical change</span><strong className={changeTone}>{change}</strong></div>
  </div>;
}

function HeroPreviewHoldings({ holdings }: { holdings: PortfolioIdea["holdings"] }) {
  return <div className="preview-holdings">
    {holdings.map((holding, index) => <div key={holding.symbol}><span className="holding-dot" style={{ background: colors[index % colors.length] }} /><b>{holding.symbol}</b><span>{holding.weight}%</span></div>)}
  </div>;
}

function HeroPreview({ idea, estimate, status }: { idea: PortfolioIdea; estimate: Estimate | null; status: HeroPreviewStatus }) {
  const title = heroCardTitles[idea.slug] ?? idea.label;
  return <div className="hero-preview" aria-label={`Ten-year historical replay preview for ${title}`}>
    <div className="preview-topline"><span>Public disclosure snapshot</span><span>{displayDate(idea.asOf)}</span></div>
    <div className="preview-title-row"><div><small>Historical replay</small><h2>{title}</h2></div><span className="preview-count">10Y</span></div>
    <p className="preview-context">$10,000 starting value · 10-year replay</p>
    <HeroPreviewChart estimate={estimate} status={status} />
    <HeroPreviewMetrics estimate={estimate} />
    <AllocationStrip holdings={idea.holdings} />
    <HeroPreviewHoldings holdings={idea.holdings} />
  </div>;
}

function QuickStartCard({ idea, onOpen }: { idea: PortfolioIdea; onOpen: (idea: PortfolioIdea) => void }) {
  const typeLabel = idea.category === "investor" ? "Public snapshot" : "Curated mix";
  return (
    <button className="quick-start-card" onClick={() => onOpen(idea)}>
      <div className="quick-start-card-top"><span className="card-label">{typeLabel}</span><ArrowRight size={16} /></div>
      <h3>{idea.label}</h3>
      <span className="quick-start-card-tag">{idea.tag}</span>
      <AllocationStrip holdings={idea.holdings} />
      <div className="quick-start-card-tickers">{idea.holdings.slice(0, 4).map((holding) => <span key={holding.symbol}>{holding.symbol} <b>{holding.weight}%</b></span>)}</div>
    </button>
  );
}

function InvestorCard({ idea, onOpen }: { idea: PortfolioIdea; onOpen: (idea: PortfolioIdea) => void }) {
  return (
    <button className="investor-card" onClick={() => onOpen(idea)}>
      <div className="card-index">0{investorIdeas.indexOf(idea) + 1}</div>
      <div className="investor-card-main">
        <span className="card-label">{idea.detail}</span>
        <h3>{idea.label}</h3>
        <PortfolioMiniChart holdings={idea.holdings} />
      </div>
      <div className="investor-card-data">
        <AllocationStrip holdings={idea.holdings} />
        <div className="ticker-list">{idea.holdings.slice(0, 6).map((holding) => <span key={holding.symbol}>{holding.symbol} <b>{holding.weight}%</b></span>)}</div>
      </div>
      <span className="card-action">Open portfolio <ArrowRight size={15} /></span>
    </button>
  );
}

function useNearViewport() {
  const elementRef = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);

  useEffect(() => {
    if (nearViewport) return;
    const element = elementRef.current;
    if (!element) return;
    if (!("IntersectionObserver" in window)) {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setNearViewport(true);
      observer.disconnect();
    }, { rootMargin: "360px 0px" });
    observer.observe(element);
    return () => observer.disconnect();
  }, [nearViewport]);

  return { elementRef, nearViewport };
}

function PortfolioMiniChart({ holdings }: { holdings: Array<{ symbol: string; weight: number }> }) {
  const { elementRef, nearViewport } = useNearViewport();
  const gradientId = useId().replace(/:/g, "");
  const [points, setPoints] = useState<number[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "empty">("idle");
  const signature = holdings.map(({ symbol, weight }) => `${symbol}:${weight}`).join("|");

  useEffect(() => {
    if (!nearViewport) return;
    let active = true;
    setStatus("loading");
    void loadPortfolioMiniChart(holdings, "1y")
      .then((payload) => {
        if (!active) return;
        if (payload.points.length < 2) {
          setPoints([]);
          setStatus("empty");
          return;
        }
        const nextPoints = payload.points.map((point) => point.index);
        setPoints(nextPoints);
        setStatus(nextPoints.length >= 2 ? "ready" : "empty");
      })
      .catch(() => { if (active) { setPoints([]); setStatus("empty"); } });
    return () => { active = false; };
  }, [holdings, nearViewport, signature]);

  if (!nearViewport) return <div ref={elementRef} className="portfolio-mini-chart portfolio-mini-chart-deferred" aria-label="Portfolio chart will load when visible"><span>Historical path</span></div>;
  if (status === "loading") return <div ref={elementRef} className="portfolio-mini-chart portfolio-mini-chart-loading" aria-label="Loading portfolio chart"><span /></div>;
  if (status === "empty" || points.length < 2) return <div ref={elementRef} className="portfolio-mini-chart portfolio-mini-chart-empty" aria-label="Portfolio chart unavailable"><span>Chart unavailable</span></div>;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const spread = Math.max(0.001, max - min);
  const coordinates = points.map((value, index) => `${((index / (points.length - 1)) * 100).toFixed(2)},${(38 - ((value - min) / spread) * 29).toFixed(2)}`).join(" ");
  const area = `0,44 ${coordinates} 100,44`;
  const positive = points.at(-1)! >= points[0];
  return <div ref={elementRef} className="portfolio-mini-chart" aria-label={`Illustrative one-year portfolio chart, ${positive ? "up" : "down"} from the start`}><svg viewBox="0 0 100 44" preserveAspectRatio="none" role="img"><defs><linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={positive ? "var(--color-accent)" : "var(--color-negative)"} stopOpacity=".24" /><stop offset="1" stopColor={positive ? "var(--color-accent)" : "var(--color-negative)"} stopOpacity="0" /></linearGradient></defs><path d="M0 12 H100 M0 26 H100" className="portfolio-mini-grid" /><polygon points={area} fill={`url(#${gradientId})`} /><polyline points={coordinates} fill="none" stroke={positive ? "var(--color-accent)" : "var(--color-negative)"} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" /></svg><span>1Y historical path</span></div>;
}

function PortfolioHistoricalChange({ holdings }: { holdings: Array<{ symbol: string; weight: number }> }) {
  const { elementRef, nearViewport } = useNearViewport();
  const [change, setChange] = useState<number | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "empty">("idle");
  const signature = holdings.map(({ symbol, weight }) => `${symbol}:${weight}`).join("|");

  useEffect(() => {
    if (!nearViewport) return;
    let active = true;
    setChange(null);
    setStatus("loading");
    void loadPortfolioMiniChart(holdings, "5y")
      .then((payload) => {
        if (!active) return;
        const returnPct = payload.returnPct;
        if (!holdings.length || returnPct === null || !Number.isFinite(returnPct)) {
          setStatus("empty");
          return;
        }
        setChange(Number(returnPct.toFixed(1)));
        setStatus("ready");
      })
      .catch(() => { if (active) setStatus("empty"); });
    return () => { active = false; };
  }, [holdings, nearViewport, signature]);

  return (
    <div ref={elementRef} className="strategy-performance" aria-label="Portfolio historical change over five years">
      <span>Historical change <em>5 years</em></span>
      <strong className={status === "ready" && change !== null ? change >= 0 ? "positive" : "negative" : ""}>
        {status === "idle" || status === "loading" ? status === "loading" ? "…" : "—" : change === null ? "—" : percent(change)}
      </strong>
    </div>
  );
}

function StrategyCard({ idea, index, onOpen }: { idea: PortfolioIdea; index: number; onOpen: (idea: PortfolioIdea) => void }) {
  return (
    <button className={`strategy-card strategy-tone-${index % 4}`} onClick={() => onOpen(idea)}>
      <div className="strategy-top"><span>{idea.tag}</span><ChevronRight size={17} /></div>
      <h3>{idea.label}</h3>
      <PortfolioMiniChart holdings={idea.holdings} />
      <AllocationStrip holdings={idea.holdings} />
      <div className="strategy-card-bottom">
        <div className="strategy-tickers">{idea.holdings.map((holding) => <span key={holding.symbol}>{holding.symbol} <b>{holding.weight}%</b></span>)}</div>
        <PortfolioHistoricalChange holdings={idea.holdings} />
      </div>
    </button>
  );
}

function AllocationTotal({ total, valid }: { total: number; valid: boolean }) {
  const remaining = 100 - total;
  return (
    <div className="allocation-total" aria-live="polite">
      <div><span>Allocated</span><strong className={valid ? "positive" : "negative"}>{total.toFixed(1).replace(".0", "")}%</strong></div>
      <div className="progress-track"><span style={{ width: `${Math.min(Math.max(total, 0), 100)}%` }} className={valid ? "valid-fill" : "invalid-fill"} /></div>
      <small>{valid ? <><Check size={12} /> Ready</> : remaining >= 0 ? `${remaining.toFixed(1).replace(".0", "")}% remaining` : `${Math.abs(remaining).toFixed(1).replace(".0", "")}% over`}</small>
    </div>
  );
}

function HoldingRow({ holding, onWeight, onRemove }: { holding: Holding; onWeight: (weight: number) => void; onRemove: () => void }) {
  return (
    <div className="holding-row">
      <span className="holding-dot" style={{ background: holding.tone }} />
      <div className="holding-symbol"><b>{holding.symbol}</b><span>{holding.name}</span></div>
      <label className="weight-control"><span className="sr-only">{holding.symbol} allocation</span><input type="number" min="0" max="100" step="0.1" value={holding.weight} onChange={(event) => onWeight(safeNumber(event.target.value, holding.weight))} /><span>%</span></label>
      <button className="icon-button remove-button" onClick={onRemove} aria-label={`Remove ${holding.symbol}`}><X size={15} /></button>
    </div>
  );
}

function StockSearch({ holdings, onAdd, compact = false }: { holdings: Holding[]; onAdd: (symbol: string, name: string) => void; compact?: boolean }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "empty" | "error">("idle");
  const requestId = useRef(0);
  const abortController = useRef<AbortController | null>(null);

  const searchSymbols = useCallback(async (trimmed: string) => {
    if (!trimmed) { setResults([]); setStatus("idle"); return; }
    const currentRequest = ++requestId.current;
    abortController.current?.abort();
    abortController.current = new AbortController();
    setStatus("loading");
    try {
      const response = await fetch(`/api/symbols/search?q=${encodeURIComponent(trimmed)}`, { signal: abortController.current.signal });
      if (!response.ok) throw new Error("Search unavailable");
      const rows = searchRows(await response.json<unknown>()).filter((row) => !holdings.some((holding) => holding.symbol === row.symbol));
      if (currentRequest !== requestId.current) return;
      setResults(rows);
      setStatus(rows.length ? "idle" : "empty");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (currentRequest !== requestId.current) return;
      setResults([]);
      setStatus("error");
    }
  }, [holdings]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) { abortController.current?.abort(); setResults([]); setStatus("idle"); return; }
    const timer = window.setTimeout(() => { void searchSymbols(trimmed); }, 220);
    return () => window.clearTimeout(timer);
  }, [query, searchSymbols]);

  function select(result: SearchResult) {
    onAdd(result.symbol, result.name);
    setQuery("");
    setResults([]);
    setStatus("idle");
  }

  return (
    <div className={`stock-search ${compact ? "stock-search-compact" : ""}`}>
      <Search size={16} />
      <input name="stock-symbol-search" aria-label="Search stocks to add" placeholder="Search ticker or company…" value={query} onChange={(event) => setQuery(event.target.value)} autoComplete="off" />
      {status === "loading" && <span className="search-status">Searching</span>}
      {(results.length > 0 || status === "empty" || status === "error") && (
        <div className="search-results">
          {results.map((result) => <button key={result.symbol} onClick={() => select(result)}><b>{result.symbol}</b><span>{result.name}</span><Plus size={15} /></button>)}
          {status === "empty" && <p>No matching symbols found.</p>}
          {status === "error" && <p>Search is temporarily unavailable.</p>}
        </div>
      )}
    </div>
  );
}

function AiMixCreator({ onStart }: { onStart: (holdings: Holding[], name: string, range: Range, aiMix?: AiMixMetadata) => void }) {
  const { data: session, ensureSession } = useAuthSession();
  const [brief, setBrief] = useState("Established U.S. companies across different parts of the economy.");
  const [style, setStyle] = useState<"steady" | "balanced" | "dynamic">("balanced");
  const [range, setRange] = useState<AiMixRange>("5y");
  const [positions, setPositions] = useState(6);
  const [exclusions, setExclusions] = useState("");
  const [status, setStatus] = useState<{ tone: "error"; text: string; upgrade?: boolean } | null>(null);
  const [creating, setCreating] = useState(false);
  const [plan, setPlan] = useState<AccountPlan | null>(null);

  useEffect(() => {
    if (!session?.user) {
      setPlan(null);
      return;
    }
    let active = true;
    void loadAccountPlan().then((value) => { if (active) setPlan(value); }).catch(() => { if (active) setPlan(null); });
    return () => { active = false; };
  }, [session?.user]);

  const aiLimitReached = Boolean(plan && plan.usage.aiMixesThisMonth >= plan.limits.aiMixesPerMonth);

  async function createMix() {
    setStatus(null);
    const currentSession = session ?? await ensureSession();
    if (!currentSession?.user) {
      requestAuth();
      setStatus({ tone: "error", text: "Sign in with Google or Apple before creating an AI mix." });
      return;
    }
    if (aiLimitReached) {
      setStatus({ tone: "error", text: plan?.isPro ? "You have used all of your Replay Pro AI mixes this month." : "You have used all 3 free AI mixes this month.", upgrade: !plan?.isPro });
      return;
    }
    setCreating(true);
    try {
      const response = await fetch("/api/ai/mix", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          prompt: brief,
          style,
          range,
          positions,
          exclusions: exclusions.split(",").map((item) => item.trim()).filter(Boolean),
        }),
      });
      const payload = await response.json<AiMixResponse | AiMixError>();
      if (!response.ok) {
        const errorPayload = payload as AiMixError;
        if (errorPayload.code === "INSUFFICIENT_HISTORY") {
          const symbols = Array.isArray(errorPayload.symbols) ? errorPayload.symbols.filter(Boolean).join(", ") : "some generated stocks";
          throw new Error(`The mix included ${symbols} without enough ${rangeLabels[range]} history. Try a shorter window, a broader brief, or exclude recently listed stocks.`);
        }
        if (errorPayload.code === "HISTORICAL_DATA_UNAVAILABLE") throw new Error("Historical stock data is temporarily unavailable. Please try again in a moment.");
        if (errorPayload.code === "AI_MONTHLY_LIMIT") {
          setStatus({ tone: "error", text: errorPayload.error ?? "You have reached this month’s AI mix limit.", upgrade: Boolean(errorPayload.upgradePath) });
          return;
        }
        throw new Error(errorPayload.error ?? "AI could not create a portfolio mix.");
      }
      const mix = payload as AiMixResponse;
      if (!mix.name || !mix.summary || !Array.isArray(mix.holdings) || !mix.holdings.length) throw new Error("AI returned an incomplete mix. Please try again.");
      onStart(
        mix.holdings.map((holding, index) => ({ symbol: holding.symbol, name: holding.name, weight: holding.weight, tone: colors[index % colors.length] })),
        mix.name,
        range,
        { summary: [mix.summary, mix.notice].filter(Boolean).join(" "), notes: mix.holdings.map(({ symbol, reason }) => ({ symbol, reason })) },
      );
      setPlan((current) => current ? { ...current, usage: { ...current.usage, aiMixesThisMonth: current.usage.aiMixesThisMonth + 1 } } : current);
    } catch (error) {
      setStatus({ tone: "error", text: error instanceof Error ? error.message : "AI could not create a portfolio mix." });
    } finally {
      setCreating(false);
    }
  }

  return (
    <section className="ai-mix-studio" aria-labelledby="ai-mix-heading">
      <div className="ai-studio-copy">
        <span className="section-label"><Sparkles size={13} /> Create with AI</span>
        <h3 id="ai-mix-heading">Start with a portfolio idea, then make it yours.</h3>
        <p>Describe the kind of mix you want to explore. AI drafts an editable set of stocks and weights; the next page shows historical close-price movement.</p>
      </div>
      <form className="ai-mix-form" onSubmit={(event) => { event.preventDefault(); void createMix(); }}>
        <label className="ai-brief"><span>What should the portfolio explore?</span><textarea value={brief} onChange={(event) => setBrief(event.target.value.slice(0, 360))} rows={2} maxLength={360} aria-describedby="ai-brief-count" /><small id="ai-brief-count">{brief.length}/360</small></label>
        <div className="ai-mix-controls">
        <fieldset className="ai-style-control"><legend>Mix style</legend><div>{(["steady", "balanced", "dynamic"] as const).map((option) => <button type="button" className={style === option ? "active" : ""} key={option} onClick={() => setStyle(option)}>{option}</button>)}</div></fieldset>
          <fieldset className="ai-style-control ai-history-control"><legend>History window</legend><div>{(["1y", "3y", "5y"] as const).map((option) => <button type="button" className={range === option ? "active" : ""} key={option} onClick={() => setRange(option)}>{rangeLabels[option]}</button>)}</div></fieldset>
          <label className="ai-select-control"><span>Positions</span><select value={positions} onChange={(event) => setPositions(Number(event.target.value))}>{[4, 6, 8].map((value) => <option key={value} value={value}>{value} stocks</option>)}</select></label>
          <label className="ai-exclusions"><span>Exclude tickers <em>optional</em></span><input value={exclusions} onChange={(event) => setExclusions(event.target.value)} placeholder="e.g. TSLA, NVDA" /></label>
        </div>
        <div className="ai-form-footer"><p><Info size={14} /> History is checked against the selected window. Newer listings may be replaced in a 5-year mix; choose 1 or 3 years to include stocks with shorter histories. This is an educational starting point, not a recommendation.</p><div className="ai-submit-group"><span className="ai-usage-count">{plan ? `${plan.usage.aiMixesThisMonth} of ${plan.limits.aiMixesPerMonth} AI mixes this month` : "3 AI mixes each month on Free"}</span><button type="submit" className="primary-button" disabled={creating || brief.trim().length < 8 || aiLimitReached}>{creating ? "Creating mix…" : "Create an AI mix"} <Sparkles size={15} /></button></div></div>
        {status && <div className="inline-message error" aria-live="polite"><Info size={14} /><span>{status.text}{status.upgrade && <> <a href={appPaths.pricing}>See Replay Pro</a></>}</span></div>}
      </form>
    </section>
  );
}

function Builder({ onStart }: { onStart: (holdings: Holding[], name: string, range: Range, aiMix?: AiMixMetadata) => void }) {
  const [holdings, setHoldings] = useState<Holding[]>(() => holdingsWithTone(investorIdeas[0].holdings));
  const [name, setName] = useState("My portfolio");
  const [range, setRange] = useState<Range>("5y");
  const total = holdings.reduce((sum, holding) => sum + holding.weight, 0);
  const valid = Math.abs(total - 100) < 0.01 && holdings.length > 0;

  function add(symbol: string, company: string) {
    if (holdings.some((holding) => holding.symbol === symbol)) return;
    setHoldings([...holdings, { symbol, name: company, weight: 0, tone: colors[holdings.length % colors.length] }]);
  }

  return (
    <section className="builder-section" id="builder">
      <div className="shell">
        <div className="section-heading builder-heading"><div><span className="section-label">Build your own</span><h2>Turn an allocation into a historical portfolio.</h2></div><p>Choose stocks, assign weights totaling 100%, and select a period. No account, trading, or rebalancing assumptions.</p></div>
        <AiMixCreator onStart={onStart} />
        <div className="builder-workbench">
          <div className="builder-editor">
            <div className="builder-name-row"><label><span>Portfolio name</span><input value={name} onChange={(event) => setName(event.target.value)} /></label><button className="reset-button" onClick={() => setHoldings([])}><RotateCcw size={14} /> Clear</button></div>
            <div className="holding-list">{holdings.map((holding) => <HoldingRow key={holding.symbol} holding={holding} onWeight={(weight) => setHoldings(holdings.map((item) => item.symbol === holding.symbol ? { ...item, weight } : item))} onRemove={() => setHoldings(holdings.filter((item) => item.symbol !== holding.symbol))} />)}</div>
            <StockSearch holdings={holdings} onAdd={add} />
            <AllocationTotal total={total} valid={valid} />
          </div>
          <aside className="builder-launch">
            <div><span className="section-label">Historical window</span><div className="range-grid">{(Object.keys(rangeLabels) as Range[]).map((key) => <button className={range === key ? "active" : ""} key={key} onClick={() => setRange(key)}>{rangeLabels[key]}</button>)}</div></div>
            <div className="launch-note"><CircleHelp size={17} /><p>This is an illustrative weighted estimate using historical close-price movement. It does not model trades, dividends, fees, taxes, or future results.</p></div>
            <button className="primary-button launch-button" disabled={!valid} onClick={() => onStart(holdings, name, range)}>Open portfolio <ArrowRight size={16} /></button>
          </aside>
        </div>
      </div>
    </section>
  );
}

function BuildPortfolioPage({ onStart }: { onStart: (holdings: Holding[], name: string, range: Range, aiMix?: AiMixMetadata) => void }) {
  if (!buildPortfolioRoute) return null;
  return (
    <PageLayout className="build-portfolio-page" fullBleed ariaLabelledBy="build-portfolio-title">
      <PageHero as="header" className="shell build-portfolio-hero">
        <span className="section-label">{buildPortfolioRoute.label}</span>
        <h1 id="build-portfolio-title">{buildPortfolioRoute.heading}</h1>
        <p>{buildPortfolioRoute.intro}</p>
      </PageHero>
      <Builder onStart={onStart} />
    </PageLayout>
  );
}

function Home({ onOpen }: { onOpen: (idea: PortfolioIdea) => void }) {
  const [heroIndex, setHeroIndex] = useState(0);
  const [heroTransition, setHeroTransition] = useState<"idle" | "out" | "in">("idle");
  const [heroEstimates, setHeroEstimates] = useState<Record<string, Estimate | null>>({});
  const [heroEstimateStatuses, setHeroEstimateStatuses] = useState<Record<string, "loading" | "ready" | "error">>({});

  useEffect(() => {
    let active = true;
    for (const idea of heroIdeas) {
      setHeroEstimateStatuses((current) => ({ ...current, [idea.slug]: current[idea.slug] ?? "loading" }));
      void loadHeroEstimate(idea).then((estimate) => {
        if (!active) return;
        setHeroEstimates((current) => ({ ...current, [idea.slug]: estimate }));
        setHeroEstimateStatuses((current) => ({ ...current, [idea.slug]: "ready" }));
      }).catch(() => {
        if (active) setHeroEstimateStatuses((current) => ({ ...current, [idea.slug]: "error" }));
      });
    }
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (heroIdeas.length < 2) return;
    let fadeOutTimer: number | undefined;
    let fadeInTimer: number | undefined;
    const rotationTimer = window.setInterval(() => {
      setHeroTransition("out");
      fadeOutTimer = window.setTimeout(() => {
        setHeroIndex((current) => (current + 1) % heroIdeas.length);
        setHeroTransition("in");
        fadeInTimer = window.setTimeout(() => setHeroTransition("idle"), 560);
      }, 340);
    }, HERO_ROTATION_INTERVAL_MS);
    return () => {
      window.clearInterval(rotationTimer);
      if (fadeOutTimer) window.clearTimeout(fadeOutTimer);
      if (fadeInTimer) window.clearTimeout(fadeInTimer);
    };
  }, []);

  const currentHeroIdea = heroIdeas[heroIndex] ?? heroIdea;
  const currentHeroStatus = heroEstimateStatuses[currentHeroIdea.slug] ?? "loading";

  return (
    <PageLayout className="home-page" fullBleed>
      <section className="hero-section">
        <div className="hero shell">
          <div className="hero-copy">
            <span className="hero-kicker"><span /> Historical portfolio simulator</span>
            <h1>What would your portfolio have become?</h1>
            <p>Explore public portfolios and replay stock mixes over time—no account, trades, or rebalancing.</p>
            <div className="hero-actions"><a className="primary-button" href={appPaths.buildPortfolio}>Build your portfolio <ArrowRight size={16} /></a><a className="secondary-button" href={appPaths.explore}>Explore portfolios</a></div>
            <div className="hero-qualifiers"><span><Check size={13} /> Manual portfolios, no sign-up</span><span><Check size={13} /> Public-data examples</span><span><Check size={13} /> Education only</span></div>
          </div>
          <div className={`hero-preview-shell ${heroTransition === "out" ? "is-fading-out" : heroTransition === "in" ? "is-fading-in" : ""}`} aria-live="polite">
            <HeroPreview idea={currentHeroIdea} estimate={heroEstimates[currentHeroIdea.slug] ?? null} status={currentHeroStatus} />
          </div>
        </div>
      </section>

      <section className="quick-start-section" id="quick-starts">
        <div className="shell">
          <div className="quick-start-heading"><div><span className="section-label">Quick starts</span><h2>Pick a mix. See it move.</h2></div><p>Open a recognizable allocation, then change the weights or period to make the replay yours.</p></div>
          <div className="quick-start-grid">{quickStartIdeas.map((idea) => <QuickStartCard key={idea.slug} idea={idea} onOpen={onOpen} />)}</div>
        </div>
      </section>

      <section className="investor-section" id="examples">
        <div className="shell">
          <div className="section-heading"><div><span className="section-label">Investor snapshots</span><h2>Start with portfolios people already follow.</h2></div><p>Every public-figure example includes its date, source, and the assumptions used to turn disclosures into a comparable allocation.</p></div>
          <div className="investor-list">{investorIdeas.map((idea) => <InvestorCard key={idea.slug} idea={idea} onOpen={onOpen} />)}</div>
        </div>
      </section>

      <section className="method-section" id="method">
        <div className="method-grid shell">
          <div><span className="section-label">How the estimate works</span><h2>Simple enough to understand. Clear enough to question.</h2></div>
          <div className="method-equation"><span>Stock movement</span><b>×</b><span>Your allocation</span><b>=</b><span>Portfolio contribution</span></div>
          <div className="method-copy"><p>Equity Replay combines each holding’s historical percentage movement using the weights you enter. The chart is an illustrative index—not a brokerage statement, a recommendation, or a prediction.</p></div>
        </div>
      </section>

      <section className="quiz-promo-section" aria-labelledby="quiz-promo-heading">
        <div className="quiz-promo shell">
          <div><span className="section-label">Finance trivia</span><h2 id="quiz-promo-heading">How well do you know your portfolio vocabulary?</h2></div>
          <div><p>Take a short stock market quiz covering ETFs, diversification, fund costs, and historical analysis. See your score, then review the reasoning.</p><a className="secondary-button" href={appPaths.stockMarketQuiz}>Take the quiz <ArrowRight size={15} /></a></div>
        </div>
      </section>

      <HomeFaq />
    </PageLayout>
  );
}

function HomeFaq() {
  return (
    <section className="home-faq-section" aria-labelledby="home-faq-heading">
      <div className="home-faq shell">
        <div className="home-faq-heading">
          <span className="section-label">Common questions</span>
          <h2 id="home-faq-heading">Understand the replay before you start.</h2>
          <p>Short answers about public portfolios, historical calculations, and what Equity Replay does not do.</p>
          <a className="secondary-button" href={appPaths.faq}>See all questions <ArrowRight size={15} /></a>
        </div>
        <div className="home-faq-list">
          {homeFaqItems.map((item) => (
            <details className="home-faq-item" key={item.question}>
              <summary>{item.question}<Plus size={16} aria-hidden="true" /></summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

type ComparisonOption = {
  slug: string;
  name: string;
  description: string;
  tag: string;
  holdings: Array<{ symbol: string; name: string; weight: number }>;
  category: "investor" | "strategy" | "community" | "custom";
  asOf?: string;
  isCustom?: boolean;
};

type ComparisonEstimateState = {
  status: "loading" | "ready" | "error";
  estimate?: Estimate;
  error?: string;
};

const comparisonStartingValue = 10_000;
const comparisonAmountFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

function parseComparisonStartingValue(params: URLSearchParams) {
  const value = safeNumber(params.get("startingValue") ?? "", comparisonStartingValue);
  return validStartingValue(value) ? Math.round(value) : comparisonStartingValue;
}

function comparisonEstimatedValue(startingValue: number, returnPct: number) {
  return startingValue * (1 + returnPct / 100);
}

function ComparisonAmountControl({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  const [draft, setDraft] = useState(() => comparisonAmountFormatter.format(value));

  useEffect(() => {
    setDraft(comparisonAmountFormatter.format(value));
  }, [value]);

  function commitDraft(nextDraft: string) {
    const nextValue = Math.round(Number(nextDraft.replace(/[^0-9]/g, "")));
    if (validStartingValue(nextValue)) {
      setDraft(comparisonAmountFormatter.format(nextValue));
      if (nextValue !== value) onChange(nextValue);
    } else {
      setDraft(comparisonAmountFormatter.format(value));
    }
  }

  return <label className="comparison-amount-control">
    <span>Starting amount</span>
    <div>
      <span aria-hidden="true">$</span>
      <input aria-label="Illustrative starting amount in U.S. dollars" type="text" inputMode="numeric" value={draft} onFocus={() => setDraft(String(value))} onChange={(event) => {
        const nextDraft = event.target.value.replace(/[^0-9]/g, "");
        setDraft(nextDraft);
        const nextValue = Math.round(Number(nextDraft));
        if (validStartingValue(nextValue)) onChange(nextValue);
      }} onBlur={() => commitDraft(draft)} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} />
    </div>
  </label>;
}

function comparisonIdea(idea: PortfolioIdea): ComparisonOption {
  return { slug: idea.slug, name: idea.label, description: idea.description, tag: idea.tag, holdings: idea.holdings, category: idea.category === "investor" ? "investor" : "strategy", asOf: idea.asOf };
}

function comparisonCommunityPortfolio(portfolio: PublicPortfolioPayload): ComparisonOption {
  return {
    slug: portfolio.slug,
    name: portfolio.name,
    description: portfolio.description || "A public historical portfolio shared with the community.",
    tag: "Published portfolio",
    holdings: portfolio.holdings.map((holding) => ({ symbol: holding.symbol, name: holding.name || holding.symbol, weight: holding.weight })),
    category: "community",
  };
}

function parseCustomComparisonOption(params: URLSearchParams, prefix: "left" | "right"): ComparisonOption | null {
  const rawHoldings = params.get(`${prefix}Holdings`);
  if (!rawHoldings) return null;
  try {
    const parsed = JSON.parse(rawHoldings) as unknown;
    if (!Array.isArray(parsed) || parsed.length < 1 || parsed.length > 12) return null;
    const holdings = parsed.map((item) => {
      if (!item || typeof item !== "object") throw new Error("Invalid holding");
      const value = item as { symbol?: unknown; name?: unknown; weight?: unknown };
      const symbol = typeof value.symbol === "string" ? value.symbol.trim().toUpperCase() : "";
      const name = typeof value.name === "string" && value.name.trim() ? value.name.trim().slice(0, 80) : symbol;
      const weight = Number(value.weight);
      if (!/^[A-Z0-9.\-]{1,12}$/.test(symbol) || !name || !Number.isFinite(weight) || weight <= 0 || weight > 100) throw new Error("Invalid holding");
      return { symbol, name, weight };
    });
    if (new Set(holdings.map((holding) => holding.symbol)).size !== holdings.length || Math.abs(holdings.reduce((sum, holding) => sum + holding.weight, 0) - 100) > 0.01) return null;
    return { slug: "custom", name: params.get(`${prefix}Name`)?.slice(0, 100) || "Current mix", description: "An editable allocation brought into a portfolio face-off.", tag: "Current mix", holdings, category: "custom", isCustom: true };
  } catch {
    return null;
  }
}

function encodeComparisonSide(params: URLSearchParams, prefix: "left" | "right", option: ComparisonOption) {
  params.set(prefix, option.isCustom ? "custom" : option.slug);
  if (option.isCustom) {
    params.set(`${prefix}Name`, option.name);
    params.set(`${prefix}Holdings`, JSON.stringify(option.holdings));
  } else {
    params.delete(`${prefix}Name`);
    params.delete(`${prefix}Holdings`);
  }
}

function comparisonPath(points: Estimate["points"], min: number, spread: number, width: number, height: number) {
  return points.map((point, index) => {
    const x = (index / Math.max(1, points.length - 1)) * width;
    const y = height - ((point.index - min + spread * 0.08) / (spread * 1.16)) * height;
    return `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
}

function comparisonPointAt(points: Estimate["points"], ratio: number) {
  return points[Math.round(ratio * Math.max(0, points.length - 1))] ?? points.at(-1);
}

function ComparisonChart({ left, right, startingValue }: { left: Estimate; right: Estimate; startingValue: number }) {
  const [hoverRatio, setHoverRatio] = useState<number | null>(null);
  const width = 900;
  const height = 330;
  const allPoints = [...left.points, ...right.points];
  const values = allPoints.map((point) => point.index);
  const min = Math.min(...values);
  const spread = Math.max(1, Math.max(...values) - min);
  const leftPath = comparisonPath(left.points, min, spread, width, height);
  const rightPath = comparisonPath(right.points, min, spread, width, height);
  const leftPoint = hoverRatio === null ? null : comparisonPointAt(left.points, hoverRatio);
  const rightPoint = hoverRatio === null ? null : comparisonPointAt(right.points, hoverRatio);
  return <div className="comparison-chart-wrap" onMouseLeave={() => setHoverRatio(null)}>
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Normalized historical comparison chart" preserveAspectRatio="none" tabIndex={0} onMouseMove={(event) => { const bounds = event.currentTarget.getBoundingClientRect(); setHoverRatio(Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width))); }} onFocus={() => setHoverRatio(0)} onBlur={() => setHoverRatio(null)} onKeyDown={(event) => { if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return; event.preventDefault(); setHoverRatio((current) => { const ratio = current ?? 0; if (event.key === "Home") return 0; if (event.key === "End") return 1; return Math.min(1, Math.max(0, ratio + (event.key === "ArrowRight" ? .04 : -.04))); }); }}>
      <path d="M0 82.5 H900 M0 165 H900 M0 247.5 H900" className="chart-grid" />
      <path d={leftPath} className="comparison-line comparison-line-left" />
      <path d={rightPath} className="comparison-line comparison-line-right" />
      {hoverRatio !== null && <line x1={hoverRatio * width} x2={hoverRatio * width} y1="0" y2={height} className="chart-hover-line" />}
    </svg>
    {leftPoint && rightPoint && <div className="comparison-tooltip" style={{ left: `${hoverRatio! * 100}%` }}><b>{displayDate(leftPoint.date)}</b><span className="comparison-tooltip-left">Portfolio A {money(startingValue * leftPoint.index / 100)} · {percent(leftPoint.index - 100)}</span><span className="comparison-tooltip-right">Portfolio B {money(startingValue * rightPoint.index / 100)} · {percent(rightPoint.index - 100)}</span></div>}
    <div className="chart-legend comparison-legend"><span><i className="comparison-legend-left" /> Portfolio A · {percent(left.returnPct)}</span><span><i className="comparison-legend-right" /> Portfolio B · {percent(right.returnPct)}</span></div>
    <div className="chart-axis"><span>{displayDate(left.points[0]?.date)}</span><span>{displayDate(left.points[Math.floor(left.points.length / 2)]?.date)}</span><span>{displayDate(left.points.at(-1)?.date)}</span></div>
  </div>;
}

function ComparisonPairBars() {
  return <div className="comparison-pair-bars" aria-hidden="true"><span className="comparison-pair-bar-a" /><span className="comparison-pair-bar-b" /></div>;
}

function PortfolioComparisonMetricCell({ state, kind, startingValue }: { state: ComparisonEstimateState; kind: "replay" | "change"; startingValue: number }) {
  if (state.status === "loading") return <span className="comparison-matrix-loading">Loading…</span>;
  if (state.status === "error" || !state.estimate) return <span className="comparison-matrix-error-text">Unavailable</span>;
  const estimate = state.estimate;
  if (kind === "replay") return <strong>{money(comparisonEstimatedValue(startingValue, estimate.returnPct))}</strong>;
  return <div className="comparison-matrix-change"><strong className={estimate.returnPct >= 0 ? "positive" : "negative"}>{percent(estimate.returnPct)}</strong><small>{rangeLabels[estimate.range]} · from {money(startingValue)}</small></div>;
}

function PortfolioComparisonMatrix({ left, right, estimates, startingValue, onOpen }: { left: ComparisonOption; right: ComparisonOption; estimates: { left: ComparisonEstimateState; right: ComparisonEstimateState }; startingValue: number; onOpen: (option: ComparisonOption) => void }) {
  return <section className="comparison-pair-card portfolio-comparison-pair-card">
    <ComparisonPairBars />
    <div className="comparison-matrix portfolio-comparison-matrix">
      <div className="comparison-matrix-head">
        <div className="comparison-matrix-corner" />
        <div className="comparison-matrix-heading comparison-matrix-heading-a"><div className="comparison-matrix-top"><span>A · {left.tag}</span><span>{left.asOf ? displayDate(left.asOf) : "Editable mix"}</span></div><h2>{left.name}</h2></div>
        <div className="comparison-matrix-heading comparison-matrix-heading-b"><div className="comparison-matrix-top"><span>B · {right.tag}</span><span>{right.asOf ? displayDate(right.asOf) : "Editable mix"}</span></div><h2>{right.name}</h2></div>
      </div>
      <div className="comparison-matrix-row comparison-matrix-allocation-row"><span>Allocation</span><div className="comparison-matrix-cell"><AllocationStrip holdings={left.holdings} /><div className="comparison-matrix-tickers">{left.holdings.slice(0, 6).map((holding) => <span key={holding.symbol}>{holding.symbol} <b>{holding.weight}%</b></span>)}</div></div><div className="comparison-matrix-cell"><AllocationStrip holdings={right.holdings} /><div className="comparison-matrix-tickers">{right.holdings.slice(0, 6).map((holding) => <span key={holding.symbol}>{holding.symbol} <b>{holding.weight}%</b></span>)}</div></div></div>
      <div className="comparison-matrix-row"><span>Replay value</span><div className="comparison-matrix-cell"><PortfolioComparisonMetricCell state={estimates.left} kind="replay" startingValue={startingValue} /></div><div className="comparison-matrix-cell"><PortfolioComparisonMetricCell state={estimates.right} kind="replay" startingValue={startingValue} /></div></div>
      <div className="comparison-matrix-row"><span>Change</span><div className="comparison-matrix-cell"><PortfolioComparisonMetricCell state={estimates.left} kind="change" startingValue={startingValue} /></div><div className="comparison-matrix-cell"><PortfolioComparisonMetricCell state={estimates.right} kind="change" startingValue={startingValue} /></div></div>
      <div className="comparison-matrix-action-row"><span>Portfolio</span><div className="comparison-matrix-cell"><button className="text-button" onClick={() => onOpen(left)}>Open and remix <ArrowRight size={14} /></button></div><div className="comparison-matrix-cell"><button className="text-button" onClick={() => onOpen(right)}>Open and remix <ArrowRight size={14} /></button></div></div>
    </div>
  </section>;
}

function ComparePage() {
  const queryString = window.location.search;
  const params = useMemo(() => new URLSearchParams(queryString), [queryString]);
  const customLeft = useMemo(() => parseCustomComparisonOption(params, "left"), [params]);
  const customRight = useMemo(() => parseCustomComparisonOption(params, "right"), [params]);
  const [leftSlug, setLeftSlug] = useState(() => params.get("left") || "warren-buffett-berkshire-core");
  const [rightSlug, setRightSlug] = useState(() => params.get("right") || "donald-trump-public-disclosure");
  const [range, setRange] = useState<Range>(() => { const value = params.get("range"); return value && value in rangeLabels ? value as Range : "5y"; });
  const [startingValue, setStartingValue] = useState(() => parseComparisonStartingValue(params));
  const [community, setCommunity] = useState<ComparisonOption[]>([]);
  const [estimates, setEstimates] = useState<{ left: ComparisonEstimateState; right: ComparisonEstimateState }>({ left: { status: "loading" }, right: { status: "loading" } });
  const [shareMessage, setShareMessage] = useState<string | null>(null);

  useEffect(() => { void loadPublicPortfolios().then((items) => setCommunity(items.map(comparisonCommunityPortfolio))).catch(() => {}); }, []);

  const options = useMemo(() => {
    const bySlug = new Map<string, ComparisonOption>();
    portfolioIdeas.forEach((idea) => bySlug.set(idea.slug, comparisonIdea(idea)));
    community.forEach((option) => { if (!bySlug.has(option.slug)) bySlug.set(option.slug, option); });
    return [...bySlug.values()];
  }, [community]);
  const left = leftSlug === "custom" ? customLeft : options.find((option) => option.slug === leftSlug);
  const right = rightSlug === "custom" ? customRight : options.find((option) => option.slug === rightSlug);
  const selectableOptions = [customLeft, customRight, ...options].filter((option, index, all): option is ComparisonOption => Boolean(option) && all.findIndex((candidate) => candidate?.slug === option?.slug && candidate?.isCustom === option?.isCustom) === index);
  const canCompare = Boolean(left && right && left !== right && left.slug !== right.slug);

  useEffect(() => {
    if (!left || !right || !canCompare) {
      setEstimates({ left: { status: "error", error: "Choose two different portfolios to compare." }, right: { status: "error", error: "Choose two different portfolios to compare." } });
      return;
    }
    let active = true;
    setEstimates({ left: { status: "loading" }, right: { status: "loading" } });
    void Promise.allSettled([
      loadPortfolioEstimate({ holdings: left.holdings.map(({ symbol, weight }) => ({ symbol, weight })), range, startingValue: comparisonStartingValue }),
      loadPortfolioEstimate({ holdings: right.holdings.map(({ symbol, weight }) => ({ symbol, weight })), range, startingValue: comparisonStartingValue }),
    ]).then(([leftResult, rightResult]) => {
      if (!active) return;
      setEstimates({
        left: leftResult.status === "fulfilled" ? { status: "ready", estimate: leftResult.value } : { status: "error", error: leftResult.reason instanceof Error ? leftResult.reason.message : "This mix could not be calculated." },
        right: rightResult.status === "fulfilled" ? { status: "ready", estimate: rightResult.value } : { status: "error", error: rightResult.reason instanceof Error ? rightResult.reason.message : "This mix could not be calculated." },
      });
    });
    return () => { active = false; };
  }, [canCompare, left, range, right]);

  function updateUrl(nextLeft: ComparisonOption, nextRight: ComparisonOption, nextRange = range, nextStartingValue = startingValue) {
    const nextParams = new URLSearchParams({ range: nextRange, startingValue: String(nextStartingValue) });
    encodeComparisonSide(nextParams, "left", nextLeft);
    encodeComparisonSide(nextParams, "right", nextRight);
    window.history.replaceState({}, "", `${appPaths.comparePortfolios}?${nextParams.toString()}`);
  }

  function changeSide(side: "left" | "right", slug: string) {
    const selected = slug === "custom" ? (side === "left" ? customLeft : customRight) : options.find((option) => option.slug === slug);
    if (!selected || !left || !right) return;
    const nextLeft = side === "left" ? selected : left;
    const nextRight = side === "right" ? selected : right;
    if (nextLeft.slug === nextRight.slug && nextLeft.isCustom === nextRight.isCustom) return;
    if (side === "left") setLeftSlug(slug); else setRightSlug(slug);
    updateUrl(nextLeft, nextRight);
  }

  function changeRange(nextRange: Range) {
    if (!left || !right) return;
    setRange(nextRange);
    updateUrl(left, right, nextRange);
  }

  function changeStartingValue(nextValue: number) {
    if (!left || !right) return;
    setStartingValue(nextValue);
    updateUrl(left, right, range, nextValue);
  }

  function shareCompare() {
    const url = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    if (!navigator.clipboard?.writeText) {
      setShareMessage("Copy is unavailable here. Use your browser’s address bar.");
      return;
    }
    void navigator.clipboard.writeText(url).then(() => setShareMessage("Face-off link copied.")).catch(() => setShareMessage("Copy is unavailable here. Use your browser’s address bar."));
  }

  function openOption(option: ComparisonOption) {
    if (option.isCustom) {
      startCustomRoute(holdingsWithTone(option.holdings), option.name, range);
    } else if (option.category === "community") {
      navigate(`/portfolio/saved/${option.slug}`);
    } else {
      navigate(`/portfolio/${option.slug}`);
    }
  }

  return <PageLayout className="compare-page">
    <PageHero className="compare-hero"><span className="section-label">Portfolio face-off</span><h1>Put two allocations on the same page.</h1><p>Compare illustrative historical close-price movement, then see what an illustrative {money(startingValue)} starting amount would have become. No trades, ownership, or rebalancing are modeled.</p></PageHero>
    <section className="compare-controls" aria-label="Choose portfolios to compare">
      <label><span>Portfolio A</span><select value={leftSlug} onChange={(event) => changeSide("left", event.target.value)}>{selectableOptions.map((option) => <option value={option.slug} key={`${option.slug}-a-${option.isCustom ? "custom" : option.category}`}>{option.name}</option>)}</select></label>
      <div className="compare-versus" aria-hidden="true">vs</div>
      <label><span>Portfolio B</span><select value={rightSlug} onChange={(event) => changeSide("right", event.target.value)}>{selectableOptions.map((option) => <option value={option.slug} key={`${option.slug}-b-${option.isCustom ? "custom" : option.category}`}>{option.name}</option>)}</select></label>
    </section>
    <div className="compare-toolbar"><div className="compare-toolbar-main"><div className="range-tabs" aria-label="Historical comparison range">{(Object.keys(rangeLabels) as Range[]).map((key) => <button key={key} className={range === key ? "active" : ""} aria-pressed={range === key} onClick={() => changeRange(key)}>{rangeLabels[key]}</button>)}</div><ComparisonAmountControl value={startingValue} onChange={changeStartingValue} /></div><button className="secondary-button" onClick={shareCompare}><Copy size={14} /> Copy face-off link</button></div>
    {shareMessage && <p className="compare-share-message" role="status">{shareMessage}</p>}
    {left && right ? <>
      <PortfolioComparisonMatrix left={left} right={right} estimates={estimates} startingValue={startingValue} onOpen={openOption} />
      {estimates.left.status === "ready" && estimates.right.status === "ready" ? <section className="comparison-chart-card"><div className="panel-heading"><div><span className="section-label">Normalized replay</span><h2>Same amount. Different paths.</h2></div><Activity size={18} /></div><ComparisonChart left={estimates.left.estimate!} right={estimates.right.estimate!} startingValue={startingValue} /><div className="methodology-callout"><Info size={15} /><p>Both series start at 100 for comparison. The illustrative starting amount is applied once at the beginning, with weights applied once at the start; no trades or rebalancing are modeled. This is an educational comparison, not a prediction or recommendation.</p></div></section> : <div className="empty-results comparison-loading"><History size={22} /><h3>Preparing the face-off.</h3><p>We’re checking the selected portfolios for enough historical close-price data.</p></div>}
    </> : <div className="empty-results"><Info size={22} /><h3>Choose two portfolios.</h3><p>The selected portfolio is unavailable or its public data has not finished loading.</p></div>}
  </PageLayout>;
}

function Explore({ onOpen }: { onOpen: (idea: PortfolioIdea) => void }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<"all" | "investor" | "strategy">("all");
  const [page, setPage] = useState(1);
  const [communityIdeas, setCommunityIdeas] = useState<PortfolioIdea[]>([]);
  useEffect(() => {
    void loadPublicPortfolios().then((portfolios) => {
      setCommunityIdeas(portfolios.map((portfolio) => ({ slug: portfolio.slug, label: portfolio.name, detail: portfolio.viewCount ? `${portfolio.viewCount} views` : "Community portfolio", tag: `${rangeLabels[portfolio.range]} portfolio`, description: portfolio.description || "A public historical portfolio shared by the community.", holdings: portfolio.holdings.map((holding) => ({ symbol: holding.symbol, name: holding.name || holding.symbol, weight: holding.weight })), accent: "#2f4537", category: "community" })));
    }).catch(() => {});
  }, []);
  const allIdeas = [...portfolioIdeas, ...communityIdeas];
  const filtered = allIdeas.filter((idea) => {
    const matchesCategory = category === "all" || (category === "investor" ? idea.category === "investor" : idea.category !== "investor");
    const haystack = `${idea.label} ${idea.description} ${idea.holdings.map((holding) => holding.symbol).join(" ")}`.toLowerCase();
    return matchesCategory && haystack.includes(query.trim().toLowerCase());
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / portfolioPageSize));
  const safePage = Math.min(page, pageCount);
  const visibleIdeas = filtered.slice((safePage - 1) * portfolioPageSize, safePage * portfolioPageSize);
  function openIdea(idea: PortfolioIdea) { idea.category === "community" ? navigate(`/portfolio/saved/${idea.slug}`) : onOpen(idea); }
  return <PageLayout className="explore-page"><PageHero as="div" className="explore-hero"><span className="section-label">{discoveryCopy.explore.label}</span><h1>{discoveryCopy.explore.title}</h1><p>{discoveryCopy.explore.description}</p></PageHero><div className="explore-toolbar"><label className="explore-search"><Search size={16} /><span className="sr-only">Search portfolios</span><input value={query} onChange={(event) => { setPage(1); setQuery(event.target.value); }} placeholder="Search by name or ticker" /></label><div className="explore-filters" role="tablist" aria-label="Portfolio categories">{(["all", "investor", "strategy"] as const).map((value) => <button key={value} role="tab" aria-selected={category === value} className={category === value ? "active" : ""} onClick={() => { setPage(1); setCategory(value); }}>{value === "all" ? "All portfolios" : value === "investor" ? "Public snapshots" : "Ideas to explore"}</button>)}</div></div><div className="explore-count"><span>{filtered.length} portfolios{filtered.length > portfolioPageSize ? ` · showing ${(safePage - 1) * portfolioPageSize + 1}–${Math.min(safePage * portfolioPageSize, filtered.length)}` : ""}</span><span>Every result is historical and illustrative</span></div><div className="explore-grid">{visibleIdeas.map((idea, index) => idea.category === "investor" ? <InvestorCard key={idea.slug} idea={idea} onOpen={openIdea} /> : <StrategyCard key={idea.slug} idea={idea} index={(safePage - 1) * portfolioPageSize + index} onOpen={openIdea} />)}</div>{filtered.length === 0 && <div className="explore-empty"><Search size={20} /><h2>No portfolio found</h2><p>Try a different ticker, theme, or category.</p></div>}<PaginationControls page={safePage} pageCount={pageCount} total={filtered.length} pageSize={portfolioPageSize} label="portfolios" onPageChange={setPage} /></PageLayout>;
}

function SavedPortfolioPage({ slug, onHome }: { slug: string; onHome: () => void }) {
  const [route, setRoute] = useState<PortfolioRoute | null>(null);
  const [status, setStatus] = useState<"loading" | "error">("loading");
  useEffect(() => {
    let active = true;
    void loadSavedPortfolio(slug)
      .then((portfolio) => { if (active) setRoute(savedPortfolioRoute(portfolio)); })
      .catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [slug]);
  useEffect(() => {
    if (route) updatePageMetadataImpl(`${route.name} — Equity Replay`, route.description || "An illustrative historical portfolio.", `/portfolio/saved/${slug}`);
  }, [route, slug]);
  if (route) return <PortfolioPage initial={route} onHome={onHome} />;
  return <PageLayout className="saved-loading"><div className="empty-results">{status === "loading" ? <><History size={22} /><h1>Opening your portfolio.</h1><p>Loading the saved allocation and its historical context.</p></> : <><Info size={22} /><h1>That portfolio is unavailable.</h1><p>It may be private, deleted, or not configured yet.</p><button className="secondary-button" onClick={onHome}>Back to Explore</button></>}</div></PageLayout>;
}

function portfolioAllocationSignature(holdings: Array<{ symbol: string; weight: number }>) {
  return [...holdings].sort((left, right) => left.symbol.localeCompare(right.symbol)).map((holding) => `${holding.symbol.toUpperCase()}:${Number(holding.weight).toFixed(1)}`).join("|");
}

function replayPreviewPath(points: Estimate["points"]) {
  const values: number[] = [];
  for (const point of points) {
    if (Number.isFinite(point.index)) values.push(point.index);
  }
  if (values.length < 2) return "M 0 32 C 10 28 18 31 27 22 S 43 26 53 18 S 74 24 84 10 S 95 17 100 8";
  const min = Math.min(...values);
  const spread = Math.max(0.001, Math.max(...values) - min);
  return values.map((value, index) => {
    const x = (index / (values.length - 1)) * 100;
    const y = 36 - ((value - min) / spread) * 28;
    return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(" ");
}

function ReplayCardPreview({ name, tag, range, startingValue, holdings, estimate }: { name: string; tag: string; range: Range; startingValue: number; holdings: Holding[]; estimate: Estimate | null }) {
  const gradientId = useId().replace(/:/g, "");
  const path = replayPreviewPath(estimate?.points ?? []);
  const endingValue = estimate ? money(estimate.endingValue) : "—";
  const change = estimate ? percent(estimate.returnPct) : "—";
  const positive = (estimate?.returnPct ?? 0) >= 0;
  const stroke = positive ? "var(--color-accent)" : "var(--color-negative)";
  const area = `${path} L 100 44 L 0 44 Z`;
  return (
    <div className="replay-card-preview" role="img" aria-label={`${name} historical portfolio replay card`}>
      <div className="replay-preview-topline"><span>{tag}</span><span className="replay-preview-credit">equityreplay.com</span></div>
      <div className="replay-preview-title-row"><div><h3>{name}</h3><span>{rangeLabels[range]} view · close-price movement</span></div></div>
      <div className="replay-preview-chart">
        <svg viewBox="0 0 100 44" preserveAspectRatio="none" aria-hidden="true">
          <defs><linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={stroke} stopOpacity=".24" /><stop offset="1" stopColor={stroke} stopOpacity="0" /></linearGradient></defs>
          <path d="M0 11 H100 M0 22 H100 M0 33 H100" className="replay-preview-grid" />
          <path d={area} fill={`url(#${gradientId})`} />
          <path d={path} className="replay-preview-line" style={{ color: stroke }} />
        </svg>
      </div>
      <div className="replay-preview-metrics">
        <div><span>Estimated value</span><strong>{endingValue}</strong></div>
        <div><span>Historical change</span><strong className={positive ? "positive" : "negative"}>{change}</strong></div>
        <div><span>Starting value</span><strong>{money(startingValue)}</strong></div>
      </div>
      <div className="replay-preview-allocation"><AllocationStrip holdings={holdings} /><div className="replay-preview-holdings">{holdings.slice(0, 8).map((holding) => <span key={holding.symbol}><b>{holding.symbol}</b><em>{holding.weight}%</em></span>)}</div></div>
      <div className="replay-preview-footer"><span>{estimate ? `Data through ${displayDate(estimate.asOf)}` : "Historical estimate pending"}</span><span>Illustrative only · no trades or rebalancing</span></div>
    </div>
  );
}

function ShareCardDialog({ name, tag, range, startingValue, holdings, estimate, onClose }: { name: string; tag: string; range: Range; startingValue: number; holdings: Holding[]; estimate: Estimate | null; onClose: () => void }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [mobileDownload, setMobileDownload] = useState(() => typeof window !== "undefined" && (window.matchMedia?.("(max-width: 800px)").matches ?? window.innerWidth <= 800));
  const svg = useMemo(() => createReplayCardSvg({
    name,
    tag,
    rangeLabel: rangeLabels[range],
    startingValue,
    endingValue: estimate?.endingValue,
    returnPct: estimate?.returnPct,
    asOf: estimate?.asOf,
    holdings,
    points: estimate?.points,
  }), [estimate, holdings, name, range, startingValue, tag]);
  const portraitSvg = useMemo(() => createReplayCardPortraitSvg({
    name,
    tag,
    rangeLabel: rangeLabels[range],
    startingValue,
    endingValue: estimate?.endingValue,
    returnPct: estimate?.returnPct,
    asOf: estimate?.asOf,
    holdings,
    points: estimate?.points,
  }), [estimate, holdings, name, range, startingValue, tag]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 800px)");
    const update = () => setMobileDownload(media.matches);
    update();
    if (media.addEventListener) media.addEventListener("change", update);
    else media.addListener(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", update);
      else media.removeListener(update);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (!dialog.open) dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; if (dialog.open) dialog.close(); };
  }, []);

  function downloadCard() {
    const downloadSvg = mobileDownload ? portraitSvg : svg;
    const url = URL.createObjectURL(new Blob([downloadSvg], { type: "image/svg+xml" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "equity-replay"}-replay${mobileDownload ? "-portrait" : ""}.svg`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <dialog ref={dialogRef} className="share-card-dialog" aria-labelledby="share-card-title" onCancel={(event) => { event.preventDefault(); onClose(); }}>
      <div className="share-card-dialog-header"><div><span className="section-label">Shareable replay card</span><h2 id="share-card-title">Take the mix with you.</h2></div><button className="icon-button" onClick={onClose} aria-label="Close share card preview"><X size={17} /></button></div>
        <ReplayCardPreview name={name} tag={tag} range={range} startingValue={startingValue} holdings={holdings} estimate={estimate} />
        <div className="share-card-dialog-footer"><p>{mobileDownload ? "Download the portrait card shown above, or use Share → Share card to attach it in X or WhatsApp." : "Download the landscape SVG poster, or use the share menu to send the portfolio link."}</p><button className="primary-button" onClick={downloadCard}><ExternalLink size={15} /> Download {mobileDownload ? "portrait card" : "card"}</button></div>
    </dialog>
  );
}

type PortfolioMessage = { tone: "success" | "error"; text: string; action?: { label: string; href: string } };
type PortfolioPageViewProps = {
  initial: PortfolioRoute;
  onHome: () => void;
  name: string;
  setName: (value: string) => void;
  titleInput: { current: HTMLTextAreaElement | null };
  holdings: Holding[];
  range: Range;
  startingValue: number;
  estimate: Estimate | null;
  loading: boolean;
  saving: boolean;
  message: PortfolioMessage | null;
  shareOpen: boolean;
  setShareOpen: (value: boolean) => void;
  reportOpen: boolean;
  setReportOpen: (value: boolean) => void;
  cardOpen: boolean;
  setCardOpen: (value: boolean) => void;
  visibility: "private" | "public";
  total: number;
  valid: boolean;
  endingValue: number;
  contributions: Estimate["contributions"];
  savePortfolio: (visibility?: "private" | "public") => void | Promise<void>;
  manageSavedPortfolio: (action: "unpublish" | "delete") => void | Promise<void>;
  reportPortfolio: (reason: "misleading" | "personalized-advice" | "wrong-source" | "other") => void | Promise<void>;
  shareSocial: (target: "x" | "whatsapp") => void | Promise<void>;
  copyLink: () => void | Promise<void>;
  openRemix: () => void;
  compareCurrent: () => void;
  changeRange: (range: Range) => void;
  updateHolding: (symbol: string, weight: number) => void;
  removeHolding: (symbol: string) => void;
  add: (symbol: string, company: string) => void;
  updateStartingValue: (value: string) => void;
  calculate: () => void | Promise<void>;
};

type PortfolioContextProps = Pick<PortfolioPageViewProps, "initial" | "name" | "setName" | "titleInput" | "holdings" | "saving" | "shareOpen" | "setShareOpen" | "reportOpen" | "setReportOpen" | "setCardOpen" | "visibility" | "savePortfolio" | "manageSavedPortfolio" | "reportPortfolio" | "shareSocial" | "copyLink" | "openRemix" | "compareCurrent">;

function PortfolioShareMenu({ initial, visibility, shareOpen, setShareOpen, reportOpen, setReportOpen, setCardOpen, manageSavedPortfolio, reportPortfolio, shareSocial, copyLink }: Pick<PortfolioContextProps, "initial" | "visibility" | "shareOpen" | "setShareOpen" | "reportOpen" | "setReportOpen" | "setCardOpen" | "manageSavedPortfolio" | "reportPortfolio" | "shareSocial" | "copyLink">) {
  return <div className="share-control">
    <button className="secondary-button" onClick={() => { setShareOpen(!shareOpen); setReportOpen(false); }} aria-expanded={shareOpen}><Share2 size={14} /> Share</button>
    {shareOpen && <div className="share-menu">
      <button onClick={() => { setCardOpen(true); setShareOpen(false); }}><Share2 size={14} /> Preview replay card</button>
      <button onClick={() => void copyLink()}><Copy size={14} /> Copy current link</button>
      {initial.canEdit && visibility === "public" && <button onClick={() => void manageSavedPortfolio("unpublish")}><Globe2 size={14} /> Unpublish from Explore</button>}
      {initial.canEdit && <button onClick={() => void manageSavedPortfolio("delete")}><X size={14} /> Delete saved portfolio</button>}
      {!initial.canEdit && visibility === "public" && <PortfolioReportOptions reportOpen={reportOpen} setReportOpen={setReportOpen} reportPortfolio={reportPortfolio} />}
      <button onClick={() => void shareSocial("x")}>Share card to X</button><button onClick={() => void shareSocial("whatsapp")}>Share card to WhatsApp</button>
    </div>}
  </div>;
}

function PortfolioReportOptions({ reportOpen, setReportOpen, reportPortfolio }: Pick<PortfolioContextProps, "reportOpen" | "setReportOpen" | "reportPortfolio">) {
  return <><button onClick={() => setReportOpen(!reportOpen)}><Flag size={14} /> Report this portfolio</button>{reportOpen && <div className="report-options"><button onClick={() => void reportPortfolio("misleading")}>Misleading description</button><button onClick={() => void reportPortfolio("personalized-advice")}>Personalized advice</button><button onClick={() => void reportPortfolio("wrong-source")}>Incorrect source</button><button onClick={() => void reportPortfolio("other")}>Other concern</button></div>}</>;
}

function PortfolioContextActions({ initial, visibility, saving, shareOpen, setShareOpen, reportOpen, setReportOpen, setCardOpen, savePortfolio, manageSavedPortfolio, reportPortfolio, shareSocial, copyLink, openRemix, compareCurrent }: Pick<PortfolioPageViewProps, "initial" | "visibility" | "saving" | "shareOpen" | "setShareOpen" | "reportOpen" | "setReportOpen" | "setCardOpen" | "savePortfolio" | "manageSavedPortfolio" | "reportPortfolio" | "shareSocial" | "copyLink" | "openRemix" | "compareCurrent">) {
  return <div className="context-actions">
    <button className="secondary-button" disabled={saving} onClick={() => void savePortfolio(initial.canEdit ? visibility : "private")}><Bookmark size={14} /> {saving ? "Saving…" : initial.canEdit ? "Save changes" : "Save"}</button>
    {visibility !== "public" && <button className="primary-button" disabled={saving} onClick={() => void savePortfolio("public")}><Globe2 size={14} /> Publish</button>}
    <button className="secondary-button" onClick={openRemix}><RotateCcw size={14} /> Remix</button>
    <button className="secondary-button" onClick={compareCurrent}><ArrowUpDown size={14} /> Compare</button>
    <PortfolioShareMenu initial={initial} visibility={visibility} shareOpen={shareOpen} setShareOpen={setShareOpen} reportOpen={reportOpen} setReportOpen={setReportOpen} setCardOpen={setCardOpen} manageSavedPortfolio={manageSavedPortfolio} reportPortfolio={reportPortfolio} shareSocial={shareSocial} copyLink={copyLink} />
    {initial.canEdit && <button className="portfolio-delete-button" onClick={() => void manageSavedPortfolio("delete")}><Trash2 size={14} /> Delete portfolio</button>}
  </div>;
}

function PortfolioContextSection({ initial, name, setName, titleInput, holdings, saving, shareOpen, setShareOpen, reportOpen, setReportOpen, setCardOpen, visibility, savePortfolio, manageSavedPortfolio, reportPortfolio, shareSocial, copyLink, openRemix, compareCurrent }: PortfolioContextProps) {
  const sourceHref = safeExternalHref(initial.sourceUrl);
  return <section className="portfolio-context">
    <div className="portfolio-context-main">
      <div className="context-meta"><span>{initial.tag}</span>{initial.asOf && <span>Snapshot · {initial.asOf}</span>}</div>
      <textarea ref={titleInput} className="portfolio-title" rows={1} value={name} onChange={(event) => setName(event.target.value)} aria-label="Portfolio name" />
      <p>{initial.description}</p>
      {initial.sourceLabel && sourceHref && <a className="source-link" href={sourceHref} target="_blank" rel="noreferrer">Source: {initial.sourceLabel} <ExternalLink size={13} /></a>}
    </div>
    <div className="context-summary">
      <div><strong>{holdings.length}</strong><span>Positions</span></div>
      <AllocationStrip holdings={holdings} />
      <PortfolioContextActions initial={initial} visibility={visibility} saving={saving} shareOpen={shareOpen} setShareOpen={setShareOpen} reportOpen={reportOpen} setReportOpen={setReportOpen} setCardOpen={setCardOpen} savePortfolio={savePortfolio} manageSavedPortfolio={manageSavedPortfolio} reportPortfolio={reportPortfolio} shareSocial={shareSocial} copyLink={copyLink} openRemix={openRemix} compareCurrent={compareCurrent} />
    </div>
  </section>;
}

function PortfolioSupportSections({ initial }: { initial: PortfolioRoute }) {
  return <>{initial.disclosureNote && <div className="disclosure-note"><Info size={15} /><span>{initial.disclosureNote}</span></div>}{initial.aiNotes && <section className="ai-composition-notes"><div className="panel-heading"><div><span className="section-label"><Sparkles size={13} /> AI draft</span><h2>Composition notes</h2></div><Info size={18} /></div><p>These are neutral notes about the draft’s intended composition. Edit the allocation freely before reviewing its historical result.</p><div className="ai-notes-grid">{initial.aiNotes.map((note) => <div key={note.symbol}><b>{note.symbol}</b><span>{note.reason}</span></div>)}</div></section>}</>;
}

function PortfolioEditorPanel({ initial, holdings, startingValue, loading, message, total, valid, updateHolding, removeHolding, add, updateStartingValue, calculate }: Pick<PortfolioPageViewProps, "initial" | "holdings" | "startingValue" | "loading" | "message" | "total" | "valid" | "updateHolding" | "removeHolding" | "add" | "updateStartingValue" | "calculate">) {
  const actionHref = message?.action ? safeInternalHref(message.action.href) : null;
  return <aside className="portfolio-editor">
    <div className="panel-heading"><div><span className="section-label">Allocation</span><h2>{initial.slug === "custom" ? "Shape the mix" : "Remix this allocation"}</h2></div><BarChart3 size={18} /></div>
    <div className="holding-list">{holdings.map((holding) => <HoldingRow key={holding.symbol} holding={holding} onWeight={(weight) => updateHolding(holding.symbol, weight)} onRemove={() => removeHolding(holding.symbol)} />)}</div>
    <StockSearch holdings={holdings} onAdd={add} compact />
    <AllocationTotal total={total} valid={valid} />
    <label className="starting-value"><span>Illustrative starting value <CircleHelp size={13} /></span><div><span>$</span><input type="number" value={startingValue} onChange={(event) => updateStartingValue(event.target.value)} min="100" max="1000000000" step="100" aria-label="Illustrative starting value" /></div>{!validStartingValue(startingValue) && <small className="field-error">Enter an amount between $100 and $1B.</small>}</label>
    <button className="primary-button update-button" disabled={loading || !valid} onClick={() => void calculate()}>{loading ? "Updating estimate…" : "Update estimate"} <History size={15} /></button>
    {message && <div className={`inline-message ${message.tone}`} aria-live="polite">{message.tone === "success" ? <Check size={14} /> : <Info size={14} />}<span>{message.text}{message.action && actionHref && <> <a href={actionHref}>{message.action.label}</a></>}</span></div>}
  </aside>;
}

function PortfolioEstimateDetails({ estimate, holdings, startingValue, endingValue, contributions }: { estimate: Estimate; holdings: Holding[]; startingValue: number; endingValue: number; contributions: Estimate["contributions"] }) {
  return <>
    <div className="metric-row"><div><span>Estimated value</span><strong>{money(endingValue)}</strong></div><div><span>Historical change</span><strong className={estimate.returnPct >= 0 ? "positive" : "negative"}>{percent(estimate.returnPct)}</strong></div><div className="metric-date"><span>Data through</span><strong>{displayDate(estimate.asOf)}</strong></div></div>
    <div className="chart-card"><Chart points={estimate.points} benchmark={estimate.benchmark} startingValue={startingValue} positive={estimate.returnPct >= 0} /><div className="chart-footnote"><span>Close-price movement · weights applied once at the start · no interim trades or rebalancing</span><span>{estimate.startDate} — {estimate.endDate} · {estimate.methodologyVersion}</span></div><div className="methodology-callout"><Info size={15} /><p>This is a weighted historical estimate using each holding’s close-price series rebased to the shared start date. It excludes dividends, fees, taxes, deposits, withdrawals, execution, and future projections. The dashed SPY line is a reference only and is not part of your allocation.</p></div></div>
    <div className="breakdown-grid">
      <div className="breakdown-card"><div className="panel-heading"><div><span className="section-label">Composition</span><h3>Current allocation</h3></div></div>{holdings.map((holding) => <div className="allocation-line" key={holding.symbol}><span className="holding-dot" style={{ background: holding.tone }} /><b>{holding.symbol}</b><span>{holding.name}</span><strong>{holding.weight}%</strong></div>)}</div>
      <div className="breakdown-card"><div className="panel-heading"><div><span className="section-label">Attribution</span><h3>What moved the estimate</h3></div><TrendingUp size={18} /></div>{contributions.map((item, index) => <div className="contribution-row" key={item.symbol}><span className="contribution-rank">{String(index + 1).padStart(2, "0")}</span><div><div><b>{item.symbol}</b><span>{item.weight}% weight</span></div><div className="contribution-bar"><span style={{ width: `${Math.min(100, Math.max(4, Math.abs(item.contributionPct) * 2))}%`, background: colors[index % colors.length] }} /></div></div><strong className={item.contributionPct >= 0 ? "positive" : "negative"}>{percent(item.contributionPct)}</strong></div>)}</div>
    </div>
  </>;
}

function PortfolioResultBody({ estimate, loading, range, holdings, startingValue, endingValue, contributions }: { estimate: Estimate | null; loading: boolean; range: Range; holdings: Holding[]; startingValue: number; endingValue: number; contributions: Estimate["contributions"] }) {
  if (estimate) return <PortfolioEstimateDetails estimate={estimate} holdings={holdings} startingValue={startingValue} endingValue={endingValue} contributions={contributions} />;
  if (loading) return <div className="empty-results loading-results"><History size={22} /><h3>Drawing the {rangeLabels[range]} view.</h3><p>Combining each holding’s historical movement with your current allocation.</p></div>;
  return <div className="empty-results"><Sparkles size={22} /><h3>Your allocation changed.</h3><p>Bring the weights back to 100%, then update the estimate to redraw the chart.</p></div>;
}

function PortfolioResultsCanvas({ range, loading, estimate, holdings, startingValue, endingValue, contributions, changeRange }: Pick<PortfolioPageViewProps, "range" | "loading" | "estimate" | "holdings" | "startingValue" | "endingValue" | "contributions" | "changeRange">) {
  return <section className="results-canvas">
    <div className="results-header"><div><span className="section-label">Historical estimate</span><h2>{loading ? `Preparing ${rangeLabels[range]}` : estimate ? `${rangeLabels[range]} view` : "Ready when you are"}</h2></div><div className="range-tabs" aria-label="Historical range">{(Object.keys(rangeLabels) as Range[]).map((key) => <button className={range === key ? "active" : ""} aria-pressed={range === key} disabled={loading} key={key} onClick={() => changeRange(key)}>{rangeLabels[key]}</button>)}</div></div>
    <PortfolioResultBody estimate={estimate} loading={loading} range={range} holdings={holdings} startingValue={startingValue} endingValue={endingValue} contributions={contributions} />
  </section>;
}

function PortfolioPageView({ initial, onHome, name, setName, titleInput, holdings, range, startingValue, estimate, loading, saving, message, shareOpen, setShareOpen, reportOpen, setReportOpen, cardOpen, setCardOpen, visibility, total, valid, endingValue, contributions, savePortfolio, manageSavedPortfolio, reportPortfolio, shareSocial, copyLink, openRemix, compareCurrent, changeRange, updateHolding, removeHolding, add, updateStartingValue, calculate }: PortfolioPageViewProps) {
  return <PageLayout className="portfolio-page">
    <button className="back-button" onClick={onHome}><ArrowLeft size={15} /> Explore portfolios</button>
    <PortfolioContextSection initial={initial} name={name} setName={setName} titleInput={titleInput} holdings={holdings} saving={saving} shareOpen={shareOpen} setShareOpen={setShareOpen} reportOpen={reportOpen} setReportOpen={setReportOpen} setCardOpen={setCardOpen} visibility={visibility} savePortfolio={savePortfolio} manageSavedPortfolio={manageSavedPortfolio} reportPortfolio={reportPortfolio} shareSocial={shareSocial} copyLink={copyLink} openRemix={openRemix} compareCurrent={compareCurrent} />
    <PortfolioSupportSections initial={initial} />
    <div className="workbench-layout">
      <PortfolioEditorPanel initial={initial} holdings={holdings} startingValue={startingValue} loading={loading} message={message} total={total} valid={valid} updateHolding={updateHolding} removeHolding={removeHolding} add={add} updateStartingValue={updateStartingValue} calculate={calculate} />
      <PortfolioResultsCanvas range={range} loading={loading} estimate={estimate} holdings={holdings} startingValue={startingValue} endingValue={endingValue} contributions={contributions} changeRange={changeRange} />
    </div>
    {cardOpen && <ShareCardDialog name={name} tag={initial.tag} range={range} startingValue={startingValue} holdings={holdings} estimate={estimate} onClose={() => setCardOpen(false)} />}
  </PageLayout>;
}

function usePortfolioPageController(initial: PortfolioRoute, onHome: () => void): PortfolioPageViewProps {
  const { data: session, ensureSession } = useAuthSession();
  const [name, setName] = useState(initial.name);
  const [holdings, setHoldings] = useState(initial.holdings);
  const [range, setRange] = useState<Range>(initial.range);
  const [startingValue, setStartingValue] = useState(initial.startingValue ?? 10000);
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<PortfolioMessage | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [visibility, setVisibility] = useState<"private" | "public">(initial.visibility ?? "private");
  const firstCalculation = useRef(true);
  const estimateRequest = useRef<{ id: number; controller: AbortController | null }>({ id: 0, controller: null });
  const titleInput = useRef<HTMLTextAreaElement>(null);
  const total = holdings.reduce((sum, holding) => sum + holding.weight, 0);
  const valid = Math.abs(total - 100) < 0.01 && holdings.length > 0 && validStartingValue(startingValue);
  const endingValue = estimate ? estimate.endingValue : startingValue;
  const contributions = useMemo(() => [...(estimate?.contributions ?? [])].sort((a, b) => b.contributionPct - a.contributionPct), [estimate]);
  const replayCardSvg = useMemo(() => createReplayCardSvg({
    name,
    tag: initial.tag,
    rangeLabel: rangeLabels[range],
    startingValue,
    endingValue: estimate?.endingValue,
    returnPct: estimate?.returnPct,
    asOf: estimate?.asOf,
    holdings,
    points: estimate?.points,
  }), [estimate, holdings, initial.tag, name, range, startingValue]);
  const replayCardPortraitSvg = useMemo(() => createReplayCardPortraitSvg({
    name,
    tag: initial.tag,
    rangeLabel: rangeLabels[range],
    startingValue,
    endingValue: estimate?.endingValue,
    returnPct: estimate?.returnPct,
    asOf: estimate?.asOf,
    holdings,
    points: estimate?.points,
  }), [estimate, holdings, initial.tag, name, range, startingValue]);
  const { fileRef: shareCardFile, statusRef: shareCardStatus } = usePreparedReplayCard({ name, landscapeSvg: replayCardSvg, portraitSvg: replayCardPortraitSvg });

  useLayoutEffect(() => {
    const element = titleInput.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [name]);

  function invalidateEstimate() {
    estimateRequest.current.id += 1;
    estimateRequest.current.controller?.abort();
    estimateRequest.current.controller = null;
  }

  const calculate = useCallback(async (nextRange = range, quiet = false) => {
    const requestId = ++estimateRequest.current.id;
    estimateRequest.current.controller?.abort();
    const controller = new AbortController();
    estimateRequest.current.controller = controller;
    if (!valid) {
      if (estimateRequest.current.id === requestId) {
        estimateRequest.current.controller = null;
        setMessage({ tone: "error", text: "Allocations must add up to 100% before updating." });
      }
      return;
    }
    setLoading(true);
    if (!quiet) setMessage(null);
    try {
      const payload = await loadPortfolioEstimate({ range: nextRange, startingValue, holdings: holdings.map(({ symbol, weight }) => ({ symbol, weight })) }, controller.signal);
      if (estimateRequest.current.id !== requestId) return;
      setEstimate(payload);
      if (!quiet) setMessage({ tone: "success", text: "Historical estimate updated." });
    } catch (error) {
      if (estimateRequest.current.id !== requestId || (error instanceof DOMException && error.name === "AbortError")) return;
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Could not calculate this estimate." });
    } finally {
      const isCurrent = estimateRequest.current.id === requestId;
      const wasInvalidatedWithoutReplacement = controller.signal.aborted && estimateRequest.current.controller === null;
      if (isCurrent) estimateRequest.current.controller = null;
      setLoading((current) => isCurrent || wasInvalidatedWithoutReplacement ? false : current);
    }
  }, [holdings, range, startingValue, valid]);

  useEffect(() => {
    if (!firstCalculation.current) return;
    firstCalculation.current = false;
    void calculate(range, true);
  }, [calculate, range]);

  function changeRange(nextRange: Range) {
    invalidateEstimate();
    setRange(nextRange);
    setEstimate(null);
    setMessage(null);
    void calculate(nextRange, true);
  }

  function updateHolding(symbol: string, weight: number) {
    invalidateEstimate();
    setHoldings(holdings.map((holding) => holding.symbol === symbol ? { ...holding, weight } : holding));
    setEstimate(null);
    setMessage(null);
  }

  function add(symbol: string, company: string) {
    if (holdings.some((holding) => holding.symbol === symbol)) return;
    invalidateEstimate();
    setHoldings([...holdings, { symbol, name: company, weight: 0, tone: colors[holdings.length % colors.length] }]);
    setEstimate(null);
    setMessage(null);
  }

  function shareUrl() {
    const unchanged = initial.slug !== "custom"
      && name.trim() === initial.name.trim()
      && range === initial.range
      && startingValue === (initial.startingValue ?? 10000)
      && portfolioAllocationSignature(holdings) === portfolioAllocationSignature(initial.holdings);
    if (unchanged && (initial.visibility === undefined || visibility === "public")) {
      return `${window.location.origin}${initial.visibility === "public" ? `/portfolio/saved/${initial.slug}` : initial.replayPath ?? `/portfolio/${initial.slug}`}`;
    }
    const params = new URLSearchParams({ name, range, startingValue: String(startingValue), holdings: JSON.stringify(holdings.map(({ symbol, name: company, weight }) => ({ symbol, name: company, weight }))) });
    if (initial.aiNotes) {
      params.set("source", "ai");
      params.set("summary", initial.description);
      params.set("notes", JSON.stringify(initial.aiNotes));
    }
    return `${window.location.origin}/portfolio/custom?${params.toString()}`;
  }

  function socialShareUrl() {
    const url = new URL(shareUrl());
    url.searchParams.set("share", "card-v2");
    return url.toString();
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl());
      setMessage({ tone: "success", text: "A link with your current allocations was copied." });
      setShareOpen(false);
    } catch {
      setMessage({ tone: "error", text: "Copy is unavailable here. Copy the page URL from your browser instead." });
    }
  }

  async function savePortfolio(visibility: "private" | "public" = "private") {
    const currentSession = session ?? await ensureSession();
    if (!currentSession?.user) {
      requestAuth();
      setMessage({ tone: "error", text: "Sign in with Google or Apple to save this portfolio." });
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const isSavedPortfolio = initial.canEdit && initial.slug !== "custom";
      const response = await fetch(isSavedPortfolio ? `/api/portfolios/${encodeURIComponent(initial.slug)}` : "/api/portfolios", {
        method: isSavedPortfolio ? "PATCH" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, description: initial.description, visibility, range, startingValue, holdings: holdings.map(({ symbol, weight }) => ({ symbol, weight })) }),
      });
      const payload = await readJson<{ error?: string; code?: string; limit?: number; url?: string; duplicate?: boolean; visibility?: "private" | "public" }>(response);
      if (!response.ok) {
        if (payload?.code === "SAVED_PORTFOLIO_LIMIT") {
          setMessage({ tone: "error", text: payload.error ?? `Free accounts can save up to ${payload.limit ?? 5} portfolios.`, action: { label: "View Replay Pro", href: appPaths.pricing } });
          return;
        }
        throw new Error(payload?.error ?? "This portfolio could not be saved.");
      }
      setVisibility(payload?.visibility ?? visibility);
      if (payload?.url) navigate(payload.url);
      else setMessage({ tone: "success", text: visibility === "public" ? "Portfolio published to Explore." : "Portfolio saved to your account." });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "This portfolio could not be saved." });
    } finally {
      setSaving(false);
    }
  }

  async function manageSavedPortfolio(action: "unpublish" | "delete") {
    if (!initial.canEdit || !initial.slug || initial.slug === "custom") return;
    if (action === "delete" && !window.confirm("Delete this saved portfolio? This cannot be undone.")) return;
    try {
      const response = await fetch(action === "delete" ? `/api/portfolios/${encodeURIComponent(initial.slug)}` : `/api/portfolios/${encodeURIComponent(initial.slug)}/unpublish`, { method: action === "delete" ? "DELETE" : "POST" });
      const payload = await readJson<{ error?: string }>(response);
      if (!response.ok) throw new Error(payload?.error ?? "This portfolio could not be updated.");
      if (action === "delete") { onHome(); return; }
      setVisibility("private");
      setMessage({ tone: "success", text: "This portfolio is now private and no longer listed in Explore." });
      setShareOpen(false);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "This portfolio could not be updated." });
    }
  }

  async function reportPortfolio(reason: "misleading" | "personalized-advice" | "wrong-source" | "other") {
    const currentSession = session ?? await ensureSession();
    if (!currentSession?.user) { requestAuth(); setMessage({ tone: "error", text: "Sign in with Google or Apple to report a portfolio." }); return; }
    try {
      const response = await fetch(`/api/portfolios/${encodeURIComponent(initial.slug)}/report`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ reason }) });
      const payload = await readJson<{ error?: string }>(response);
      if (!response.ok) throw new Error(payload?.error ?? "This portfolio could not be reported.");
      setMessage({ tone: "success", text: "Thanks. The portfolio has been sent for review." });
      setReportOpen(false);
      setShareOpen(false);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "This portfolio could not be reported." });
    }
  }

  async function shareSocial(target: "x" | "whatsapp") {
    const url = socialShareUrl();
    const text = `${name} — explore this historical portfolio`;
    if (shareCardStatus.current === "preparing") {
      setMessage({ tone: "success", text: "Preparing the replay card. Try sharing again in a moment." });
      return;
    }
    try {
      const result = shareCardFile.current ? await shareReplayCard({ file: shareCardFile.current, title: name, text, url }) : "unsupported";
      if (result === "shared") {
        setShareOpen(false);
        setMessage({ tone: "success", text: "Replay card ready to share. Choose X or WhatsApp from the share sheet." });
        return;
      }
      if (result === "cancelled") return;
    } catch {
      // Fall through to the platform link when image sharing is unavailable.
    }

    if (target === "x") {
      const intentText = encodeURIComponent(`${name} — a historical portfolio`);
      window.open(`https://twitter.com/intent/tweet?text=${intentText}&url=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text}: ${url}`)}`, "_blank", "noopener,noreferrer");
    }
    setShareOpen(false);
  }

  function openRemix() {
    document.querySelector(".portfolio-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMessage({ tone: "success", text: "Adjust the allocation, then update the historical estimate." });
  }

  function compareCurrent() {
    const defaultRight = initial.slug === "donald-trump-public-disclosure" ? "warren-buffett-berkshire-core" : "donald-trump-public-disclosure";
    const unchanged = initial.slug !== "custom"
      && name.trim() === initial.name.trim()
      && range === initial.range
      && startingValue === (initial.startingValue ?? 10000)
      && portfolioAllocationSignature(holdings) === portfolioAllocationSignature(initial.holdings);
    const params = new URLSearchParams({ left: unchanged ? initial.slug : "custom", right: defaultRight, range });
    if (!unchanged) {
      params.set("leftName", name);
      params.set("leftStartingValue", String(startingValue));
      params.set("leftHoldings", JSON.stringify(holdings.map(({ symbol, name: company, weight }) => ({ symbol, name: company, weight }))));
    }
    navigate(`${appPaths.comparePortfolios}?${params.toString()}`);
  }

  return {
    initial,
    onHome,
    name,
    setName,
    titleInput,
    holdings,
    range,
    startingValue,
    estimate,
    loading,
    saving,
    message,
    shareOpen,
    setShareOpen,
    reportOpen,
    setReportOpen,
    cardOpen,
    setCardOpen,
    visibility,
    total,
    valid,
    endingValue,
    contributions,
    savePortfolio,
    manageSavedPortfolio,
    reportPortfolio,
    shareSocial,
    copyLink,
    openRemix,
    compareCurrent,
    changeRange,
    updateHolding,
    removeHolding: (symbol) => { invalidateEstimate(); setHoldings(holdings.filter((item) => item.symbol !== symbol)); setEstimate(null); },
    add,
    updateStartingValue: (value) => { invalidateEstimate(); setStartingValue(safeNumber(value, startingValue)); setEstimate(null); },
    calculate: () => void calculate(),
  };
}

function PortfolioPage({ initial, onHome }: { initial: PortfolioRoute; onHome: () => void }) {
  return <PortfolioPageView {...usePortfolioPageController(initial, onHome)} />;
}

const featuredCalculators = featuredCalculatorSlugs
  .map((slug) => findCalculator(slug))
  .filter((calculator): calculator is NonNullable<typeof calculator> => Boolean(calculator));

const footerGuideLinks = [
  { slug: "what-is-dollar-cost-averaging", label: "Dollar-cost averaging" },
  { slug: "how-to-read-an-etf-factsheet", label: "Read an ETF factsheet" },
  { slug: "brokerage-account-vs-portfolio-simulator", label: "Brokerage account vs simulator" },
] as const;

function Footer({ onHome, theme, onThemeChange }: { onHome: () => void; theme: ThemePreference; onThemeChange: (theme: ThemePreference) => void }) {
  return <footer className="site-footer"><div className="footer-inner shell"><div className="footer-brand"><Logo onClick={onHome} /><p>Replay historical stock allocations with a clear starting point.</p><ProductHuntBadge /><a href="mailto:team@equityreplay.com">team@equityreplay.com</a></div><nav className="footer-nav-group" aria-label="Explore"><span>Explore</span><a href={appPaths.stocks}>Stocks</a><a href={appPaths.compareStocks}>Compare stocks</a><a href={appPaths.explore}>Portfolios</a><a href={appPaths.comparePortfolios}>Compare portfolios</a></nav><nav className="footer-nav-group" aria-label="Public investors"><span>Public investors</span><a href={appPaths.explore}>All public portfolios</a>{publicInvestorLinks.map((investor) => <a key={investor.slug} href={`/portfolio/${investor.slug}`}>{investor.label}</a>)}</nav><nav className="footer-nav-group" aria-label="Product"><span>Product</span><a href={appPaths.buildPortfolio}>Build a portfolio</a><a href={appPaths.stockMarketQuiz}>Stock market quiz</a><a href={appPaths.pricing}>Replay Pro</a><a href={appPaths.faq}>FAQ</a><a href={appPaths.blog}>Journal</a><a href={appPaths.methodology}>How it works</a><a href={appPaths.contact}>Contact</a></nav><nav className="footer-nav-group" aria-label="Financial calculators"><span>Calculators</span><a href={appPaths.calculators}>All calculators</a>{featuredCalculators.map((calculator) => <a key={calculator.slug} href={`${appPaths.calculators}/${calculator.slug}`}>{calculator.name}</a>)}</nav><nav className="footer-nav-group" aria-label="Guides"><span>Guides</span>{footerGuideLinks.map((guide) => <a key={guide.slug} href={`${appPaths.blog}/${guide.slug}`}>{guide.label}</a>)}</nav><nav className="footer-nav-group" aria-label="Account"><span>Account</span><a href={appPaths.login}>Log in</a><a href={appPaths.account}>Account</a><a href={appPaths.portfolios}>Saved portfolios</a></nav></div><div className="footer-bottom shell"><div className="footer-legal"><span>© 2026 Quadri LLC. All rights reserved.</span><span>Historical data · illustrative only · no trades or rebalancing</span><a href={appPaths.terms}>Terms</a><a href={appPaths.privacy}>Privacy</a></div><ThemeControl theme={theme} onChange={onThemeChange} /></div></footer>;
}

function resolvePortfolio(path: string): PortfolioRoute | null {
  const [pathname, queryString] = path.split("?");
  const segments = pathname.split("/").filter(Boolean);
  const isReplay = segments[0] === "replay";
  const slug = segments[1];
  const requestedRange = isReplay ? segments[2] : undefined;
  if (slug && slug !== "custom") {
    const idea = findPortfolio(slug);
    if (idea) {
      const range = requestedRange && requestedRange in rangeLabels ? requestedRange as Range : "5y";
      return { name: idea.label, description: idea.description, tag: idea.tag, slug: idea.slug, holdings: holdingsWithTone(idea.holdings), range, sourceLabel: idea.sourceLabel, sourceUrl: idea.sourceUrl, asOf: idea.asOf, disclosureNote: idea.disclosureNote, replayPath: isReplay ? `/replay/${idea.slug}/${range}` : undefined };
    }
  }
  if (slug === "custom") {
    try {
      const params = new URLSearchParams(queryString);
      const rawHoldings = JSON.parse(params.get("holdings") ?? "[]") as unknown;
      if (!Array.isArray(rawHoldings) || rawHoldings.length < 1 || rawHoldings.length > 12) return null;
      const parsedHoldings = rawHoldings.map((holding) => {
        if (!holding || typeof holding !== "object") throw new Error("Invalid holding");
        const item = holding as { symbol?: unknown; name?: unknown; weight?: unknown };
        const symbol = typeof item.symbol === "string" ? item.symbol.trim().toUpperCase() : "";
        const name = typeof item.name === "string" ? item.name.trim().slice(0, 80) : symbol;
        const weight = Number(item.weight);
        if (!/^[A-Z0-9.\-]{1,12}$/.test(symbol) || !name || name.length > 80 || !Number.isFinite(weight) || weight <= 0 || weight > 100) throw new Error("Invalid holding");
        return { symbol, name, weight };
      });
      if (new Set(parsedHoldings.map((holding) => holding.symbol)).size !== parsedHoldings.length) return null;
      if (Math.abs(parsedHoldings.reduce((total, holding) => total + holding.weight, 0) - 100) > 0.01) return null;
      const startingValue = Number(params.get("startingValue") ?? 10000);
      if (!validStartingValue(startingValue)) return null;
      const range = params.get("range") as Range;
      const source = params.get("source");
      const rawNotes = JSON.parse(params.get("notes") ?? "[]") as Array<{ symbol?: string; reason?: string }>;
      const aiNotes = source === "ai" ? rawNotes.reduce<Array<{ symbol: string; reason: string }>>((notes, note) => {
        if (typeof note.symbol === "string" && typeof note.reason === "string" && note.symbol.length <= 12 && note.reason.length <= 180) notes.push({ symbol: note.symbol.toUpperCase(), reason: note.reason });
        return notes;
      }, []) : undefined;
      const summary = params.get("summary");
      return { name: (params.get("name") ?? "My portfolio").slice(0, 100), description: source === "ai" && summary ? summary.slice(0, 360) : "Your own allocation, ready to review and reshape.", tag: source === "ai" ? "AI-generated mix" : "Custom portfolio", slug: "custom", holdings: holdingsWithTone(parsedHoldings), range: range in rangeLabels ? range : "5y", aiNotes, startingValue };
    } catch { return null; }
  }
  return null;
}

function NotFoundPage({ path }: { path: string }) {
  return <PageLayout className="not-found-page" ariaLabelledBy="not-found-title"><div className="not-found-copy"><span className="hero-kicker"><span /> 404 · Route not found</span><h1 id="not-found-title">This page fell off the chart.</h1><p>We couldn’t find that address. Try heading back to the replay index or explore a portfolio that still has a path.</p><div className="not-found-actions"><a className="primary-button" href={appPaths.home}>Return home <ArrowRight size={15} /></a><a className="secondary-button" href={appPaths.explore}>Explore portfolios</a></div></div><div className="not-found-art" aria-hidden="true"><div className="not-found-art-top"><span>REQUEST STATUS</span><strong>404</strong></div><svg viewBox="0 0 520 220" preserveAspectRatio="xMidYMid meet" role="presentation"><path d="M0 42H520M0 110H520M0 178H520" /><path d="M0 156 58 146 96 158 146 112 194 130 238 74 282 94 324 42 372 68 418 28 466 54 520 16" className="not-found-line" /><circle cx="520" cy="16" r="6" /></svg><div className="not-found-art-bottom"><span>Requested address</span><code>{path}</code></div></div></PageLayout>;
}

type AppRouteState = {
  path: string;
  pathname: string;
  savedMatch: RegExpMatchArray | null;
  portfolio: PortfolioRoute | null;
  blogPost: ReturnType<typeof findBlogPost>;
  stockDetailMatch: RegExpMatchArray | null;
  calculatorRouteMatch: RegExpMatchArray | null;
  calculator: ReturnType<typeof findCalculator> | null;
};

type AppRouteActions = {
  onHome: () => void;
  onOpenIdea: (idea: PortfolioIdea) => void;
  onOpenStock: (symbol: string) => void;
  onStart: (holdings: Holding[], name: string, range: Range, aiMix?: AiMixMetadata) => void;
};

function renderAppRoute(state: AppRouteState, actions: AppRouteActions): ReactNode {
  const { path, pathname, savedMatch, portfolio, blogPost, stockDetailMatch, calculatorRouteMatch, calculator } = state;
  const { onHome, onOpenIdea, onOpenStock, onStart } = actions;

  if (savedMatch) return <SavedPortfolioPage key={savedMatch[1]} slug={savedMatch[1]} onHome={onHome} />;
  if (blogPost) return <BlogPostPage key={blogPost.slug} post={blogPost} />;

  switch (pathname) {
    case appPaths.login:
    case appPaths.signIn:
      return <LoginPage />;
    case appPaths.account:
      return <AccountPage />;
    case appPaths.pricing:
      return <PricingPage />;
    case appPaths.moderation:
      return <DeferredPage><LazyModerationPage /></DeferredPage>;
    case appPaths.admin:
      return <DeferredPage><LazyAdminPage /></DeferredPage>;
    case appPaths.portfolios:
      return <PortfoliosPage />;
    case appPaths.terms:
      return <DeferredPage><LazyLegalPage kind="terms" /></DeferredPage>;
    case appPaths.privacy:
      return <DeferredPage><LazyLegalPage kind="privacy" /></DeferredPage>;
    case appPaths.methodology:
      return <DeferredPage><LazyMethodologyPage /></DeferredPage>;
    case appPaths.faq:
      return <DeferredPage><LazyFaqPage /></DeferredPage>;
    case appPaths.contact:
      return <DeferredPage><LazyContactPage /></DeferredPage>;
    case appPaths.buildPortfolio:
      return buildPortfolioRoute ? <BuildPortfolioPage onStart={onStart} /> : <NotFoundPage path={pathname} />;
    case appPaths.stockMarketQuiz:
      return stockMarketQuizRoute ? <DeferredPage><LazyStockMarketQuizPage /></DeferredPage> : <NotFoundPage path={pathname} />;
    case appPaths.blog:
      return <BlogIndexPage />;
    case appPaths.stocks:
      return <DeferredPage><LazyStockExplorerPage onOpenStock={onOpenStock} /></DeferredPage>;
    case appPaths.compareStocks:
      return <DeferredPage><LazyStockComparisonPage key={path} onOpenStock={onOpenStock} /></DeferredPage>;
    case appPaths.comparePortfolios:
      return <ComparePage key={path} />;
    case appPaths.explore:
      return <Explore onOpen={onOpenIdea} />;
    case appPaths.home:
      return <Home onOpen={onOpenIdea} />;
    default:
      break;
  }

  if (calculatorRouteMatch && (!calculatorRouteMatch[1] || calculator)) {
    return <DeferredPage><LazyCalculatorsPage key={calculatorRouteMatch[1] ?? "hub"} slug={calculatorRouteMatch[1]} /></DeferredPage>;
  }
  if (stockDetailMatch) {
    return <DeferredPage><LazyStockDetailPage key={stockDetailMatch[1]} symbol={stockDetailMatch[1]} onStartPortfolio={onStart} chartComponent={Chart} /></DeferredPage>;
  }
  if (portfolio) return <PortfolioPage key={path} initial={portfolio} onHome={onHome} />;
  return <NotFoundPage path={pathname} />;
}

function AppPremium() {
  const [path, setPath] = useState(window.location.pathname + window.location.search);
  const [theme, setTheme] = useState<ThemePreference>(readThemePreference);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname + window.location.search);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme === "system" ? "light dark" : theme;
    try { window.localStorage.setItem("equity-replay-theme", theme); } catch { /* Storage can be unavailable in private browsing. */ }
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const applyThemeColor = () => {
      const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (themeColor) themeColor.content = (theme === "light" || (theme === "system" && media.matches)) ? "#f4f6ee" : "#11130f";
    };
    applyThemeColor();
    media.addEventListener("change", applyThemeColor);
    return () => media.removeEventListener("change", applyThemeColor);
  }, [theme]);
  const {
    savedMatch,
    pathname,
    portfolio,
    blogPost,
    stockDetailMatch,
    stockDetail,
    replayRouteMatch,
    replayIdea,
    calculatorRouteMatch,
    calculator,
  } = useMemo(() => {
    const savedMatch = path.match(/^\/portfolio\/saved\/([A-Za-z0-9_-]{8,64})/);
    const pathname = path.split("?")[0].replace(/\/+$/, "") || "/";
    const portfolio = (path.startsWith("/portfolio/") || path.startsWith("/replay/")) && !savedMatch ? resolvePortfolio(path) : null;
    const blogPost = appPaths.blog && pathname.startsWith(`${appPaths.blog}/`) ? findBlogPost(pathname.slice(`${appPaths.blog}/`.length).replace(/\/$/, "")) : null;
    const stockDetailMatch = pathname.match(stockDetailRoutePattern);
    const stockDetail = stockDetailMatch ? findStock(stockDetailMatch[1]) : null;
    const replayRouteMatch = pathname.match(/^\/replay\/([A-Za-z0-9-]+)(?:\/(1m|6m|1y|3y|5y|10y))?\/?$/);
    const replayIdea = replayRouteMatch ? findPortfolio(replayRouteMatch[1]) : null;
    const calculatorRouteMatch = pathname.match(calculatorRoutePattern);
    const calculator = calculatorRouteMatch ? findCalculator(calculatorRouteMatch[1]) : null;
    return { savedMatch, pathname, portfolio, blogPost, stockDetailMatch, stockDetail, replayRouteMatch, replayIdea, calculatorRouteMatch, calculator };
  }, [path]);
  useEffect(() => {
    const updatePageMetadata = (title: string, description: string, canonicalPath: string | null, structuredData?: Record<string, unknown>, robots = "index,follow", ogType = "website", analyticsPath?: string) => updatePageMetadataImpl(title, description, canonicalPath, structuredData, robots, ogType, analyticsPath, path);
    if (savedMatch) return;
    if (pathname === appPaths.blog) updatePageMetadata("Stock portfolio guides and historical replays — Equity Replay", "Clear, educational guides to stock portfolio allocation, historical performance, public filings, and what-if portfolios.", appPaths.blog, { "@context": "https://schema.org", "@type": "CollectionPage", name: "Stock portfolio guides and historical replays", description: "Clear, educational guides to stock portfolio allocation, historical performance, public filings, and what-if portfolios.", url: `${window.location.origin}${appPaths.blog}` });
    else if (blogPost) updatePageMetadata(`${blogPost.title} — Equity Replay`, blogPost.description, `${appPaths.blog}/${blogPost.slug}`, { "@context": "https://schema.org", "@type": "Article", headline: blogPost.title, description: blogPost.description, datePublished: blogPost.publishedAt, dateModified: blogPost.updatedAt, author: { "@type": "Organization", name: "Equity Replay" }, publisher: { "@type": "Organization", name: "Equity Replay" }, mainEntityOfPage: `${window.location.origin}${appPaths.blog}/${blogPost.slug}`, keywords: blogPost.keywords.join(", ") }, "index,follow", "article");
    else if (pathname === appPaths.terms) updatePageMetadata("Terms of Use — Equity Replay", "Read the terms for using Equity Replay, operated by Quadri LLC, including historical simulator, AI Mix, account, billing, and public-content rules.", appPaths.terms, { "@context": "https://schema.org", "@type": "WebPage", name: "Terms of Use", description: "Read the terms for using Equity Replay, operated by Quadri LLC, including historical simulator, AI Mix, account, billing, and public-content rules.", url: `${window.location.origin}${appPaths.terms}`, publisher: { "@type": "Organization", name: "Quadri LLC", url: window.location.origin } });
    else if (pathname === appPaths.privacy) updatePageMetadata("Privacy Policy — Equity Replay", "Learn how Quadri LLC handles account, portfolio, AI Mix, billing, calculator, and technical information for Equity Replay.", appPaths.privacy, { "@context": "https://schema.org", "@type": "WebPage", name: "Privacy Policy", description: "Learn how Quadri LLC handles account, portfolio, AI Mix, billing, calculator, and technical information for Equity Replay.", url: `${window.location.origin}${appPaths.privacy}`, publisher: { "@type": "Organization", name: "Quadri LLC", url: window.location.origin } });
    else if (pathname === appPaths.methodology) updatePageMetadata("Historical Portfolio Simulator Methodology — Equity Replay", "Understand how Equity Replay uses close prices, fixed starting weights, shared dates, and historical windows to create educational portfolio replays.", appPaths.methodology, { "@context": "https://schema.org", "@type": "TechArticle", headline: "Historical Portfolio Simulator Methodology — Equity Replay", description: "Understand how Equity Replay uses close prices, fixed starting weights, shared dates, and historical windows to create educational portfolio replays.", url: `${window.location.origin}${appPaths.methodology}`, dateModified: "2026-08-13", author: { "@type": "Organization", name: "Equity Replay" } });
    else if (pathname === appPaths.faq) updatePageMetadata("FAQ — Equity Replay", "Get clear answers about historical portfolio replays, public portfolios, stock and ETF data, Replay Pro, accounts, and calculation methods.", appPaths.faq, faqStructuredData(`${window.location.origin}${appPaths.faq}`));
    else if (pathname === appPaths.contact) updatePageMetadata("Contact Equity Replay — Questions, help, and feedback", "Find answers in the Equity Replay FAQ, Journal, and pricing pages, or email team@equityreplay.com.", appPaths.contact, { "@context": "https://schema.org", "@type": "ContactPage", name: "Contact Equity Replay", description: "Find answers in the Equity Replay FAQ, Journal, and pricing pages, or email team@equityreplay.com.", url: `${window.location.origin}${appPaths.contact}`, mainEntity: { "@type": "Organization", name: "Equity Replay", url: window.location.origin, email: "team@equityreplay.com" } });
    else if (buildPortfolioRoute && pathname === appPaths.buildPortfolio) updatePageMetadata(buildPortfolioRoute.title, buildPortfolioRoute.description, buildPortfolioRoute.path, { "@context": "https://schema.org", "@type": "WebPage", name: buildPortfolioRoute.structuredName, description: buildPortfolioRoute.description, url: `${window.location.origin}${buildPortfolioRoute.path}`, isPartOf: { "@type": "WebApplication", name: "Equity Replay", url: window.location.origin } });
    else if (stockMarketQuizRoute && pathname === appPaths.stockMarketQuiz) {
      const metadata = getStockMarketQuizSeoMetadata(`${window.location.origin}${stockMarketQuizRoute.path}`, window.location.origin);
      if (metadata) updatePageMetadata(metadata.title, metadata.description, metadata.canonicalPath, metadata.structuredData);
    }
    else if (calculatorRouteMatch && (!calculatorRouteMatch[1] || calculator)) {
      const canonicalPath = calculator ? `${appPaths.calculators}/${calculator.slug}` : appPaths.calculators;
      const title = calculator?.seoTitle ?? "Financial Calculators — Equity Replay";
      const description = calculator?.seoDescription ?? "Use simple financial calculators for investing, saving, borrowing, retirement, debt payoff, housing decisions, and historical return scenarios.";
      const hasQuery = new URLSearchParams(path.split("?")[1] ?? "").size > 0;
      updatePageMetadata(title, description, canonicalPath, { "@context": "https://schema.org", "@type": calculator ? "WebPage" : "CollectionPage", name: title, description, url: `${window.location.origin}${canonicalPath}`, isPartOf: { "@type": "WebApplication", name: "Equity Replay", url: window.location.origin }, ...(calculator ? { mainEntity: { "@type": "WebApplication", name: calculator.title, applicationCategory: "FinanceApplication", operatingSystem: "Web" } } : { mainEntity: { "@type": "ItemList", itemListElement: calculatorCatalog.map((item, index) => ({ "@type": "ListItem", position: index + 1, url: `${window.location.origin}${appPaths.calculators}/${item.slug}`, name: item.title })) } }) }, hasQuery ? "noindex,follow" : "index,follow");
    }
    else if (pathname === appPaths.login || pathname === appPaths.signIn) updatePageMetadata("Log in — Equity Replay", "Log in to manage your Equity Replay portfolios.", appPaths.login, undefined, "noindex,follow");
    else if (pathname === appPaths.account) updatePageMetadata("Account — Equity Replay", "Manage your Equity Replay account.", appPaths.account, undefined, "noindex,follow");
    else if (pathname === appPaths.moderation) updatePageMetadata("Moderation — Equity Replay", "Review reported community portfolios.", appPaths.moderation, undefined, "noindex,follow");
    else if (pathname === appPaths.admin) updatePageMetadata("Admin dashboard — Equity Replay", "Private application growth dashboard.", appPaths.admin, undefined, "noindex,nofollow");
    else if (pathname === appPaths.portfolios) updatePageMetadata("Saved portfolios — Equity Replay", "Manage your saved Equity Replay portfolios.", appPaths.portfolios, undefined, "noindex,follow");
    else if (pathname === appPaths.pricing) updatePageMetadata("Replay Pro Pricing — Equity Replay", "Simple Equity Replay pricing: explore and simulate historical portfolios for free, or unlock more saved portfolios and AI mixes with Replay Pro.", appPaths.pricing, { "@context": "https://schema.org", "@type": "WebPage", name: "Replay Pro pricing", description: "Simple Equity Replay pricing: explore and simulate historical portfolios for free, or unlock more saved portfolios and AI mixes with Replay Pro.", url: `${window.location.origin}${appPaths.pricing}` });
    else if (pathname === appPaths.explore) updatePageMetadata("Explore portfolios — Equity Replay", "Browse public portfolio snapshots built from disclosed holdings, then replay how each stock mix moved across a selected historical window.", appPaths.explore, { "@context": "https://schema.org", "@type": "CollectionPage", name: "Explore historical portfolios", description: "Browse public portfolio snapshots built from disclosed holdings, then replay how each stock mix moved across a selected historical window.", url: `${window.location.origin}${appPaths.explore}` });
    else if (pathname === appPaths.compareStocks) {
      const hasQuery = new URLSearchParams(path.split("?")[1] ?? "").size > 0;
      updatePageMetadata("Compare Historical Stocks — Equity Replay", "Compare two stocks or ETFs across the same historical close-price window and see what a chosen starting amount would have become.", appPaths.compareStocks, { "@context": "https://schema.org", "@type": "WebPage", name: "Compare historical stocks", description: "Compare two stocks or ETFs across the same historical close-price window and see what a chosen starting amount would have become.", url: `${window.location.origin}${appPaths.compareStocks}`, isPartOf: { "@type": "WebApplication", name: "Equity Replay", url: window.location.origin } }, hasQuery ? "noindex,follow" : "index,follow");
    }
    else if (pathname === appPaths.comparePortfolios) updatePageMetadata("Compare Historical Portfolios — Equity Replay", "Compare two illustrative stock allocations across the same historical window and see what a chosen starting amount would have become.", appPaths.comparePortfolios, { "@context": "https://schema.org", "@type": "WebPage", name: "Compare historical portfolios", description: "Compare two illustrative stock allocations across the same historical window and see what a chosen starting amount would have become.", url: `${window.location.origin}${appPaths.comparePortfolios}` });
    else if (pathname === appPaths.stocks) updatePageMetadata("Explore Stocks Through Time — Equity Replay", "Compare historical close-price movement across stocks and ETFs through six educational time windows.", appPaths.stocks, { "@context": "https://schema.org", "@type": "CollectionPage", name: "Explore stocks through time", description: "Compare historical close-price movement across stocks and ETFs through six educational time windows.", url: `${window.location.origin}${appPaths.stocks}` });
    else if (stockDetailMatch) {
      const displaySymbol = stockDetailMatch[1].toUpperCase();
      const stockName = stockDetail?.name ?? displaySymbol;
      updatePageMetadata(`${stockName} (${displaySymbol}) — Equity Replay`, "Explore illustrative historical close-price movement across a selected window.", `${appPaths.stocks}/${displaySymbol}`, { "@context": "https://schema.org", "@type": "WebPage", name: `${stockName} (${displaySymbol}) historical close prices`, description: "Explore illustrative historical close-price movement across a selected window.", url: `${window.location.origin}${appPaths.stocks}/${displaySymbol}` }, stockDetail ? "index,follow" : "noindex,follow");
    }
    else if (replayIdea && replayRouteMatch) {
      const replayRange = (replayRouteMatch[2] ?? "5y") as Range;
      const replayPath = `/replay/${replayIdea.slug}/${replayRange}`;
      updatePageMetadata(`${replayIdea.label} — ${rangeLabels[replayRange]} historical replay | Equity Replay`, `Replay ${replayIdea.label} over ${rangeLabels[replayRange]} using historical close prices and fixed allocation weights. Illustrative only; no trades or rebalancing.`, replayPath, { "@context": "https://schema.org", "@type": "WebPage", name: `${replayIdea.label} ${rangeLabels[replayRange]} historical replay`, description: replayIdea.description, url: `${window.location.origin}${replayPath}` });
    }
    else if (portfolio) updatePageMetadata(`${portfolio.name} — Equity Replay`, portfolio.description, portfolio.slug === "custom" ? appPaths.home : portfolio.replayPath ?? `/portfolio/${portfolio.slug}`, undefined, portfolio.slug === "custom" ? "noindex,follow" : "index,follow");
    else if (pathname === appPaths.home) updatePageMetadata("Equity Replay — Historical Stock Portfolio Simulator", "Build a stock or ETF allocation, explore public portfolios, and replay historical price movement across months or years. No account or trades are required.", appPaths.home, faqStructuredData(`${window.location.origin}${appPaths.home}`, homeFaqItems));
    else updatePageMetadata("Page not found — Equity Replay", "The Equity Replay page you requested could not be found.", null, undefined, "noindex,follow");
  }, [path, portfolio, savedMatch, pathname, blogPost, stockDetailMatch, stockDetail, replayIdea, replayRouteMatch, calculatorRouteMatch, calculator]);
  const routeContent = renderAppRoute({ path, pathname, savedMatch, portfolio, blogPost, stockDetailMatch, calculatorRouteMatch, calculator }, { onHome: goHomeRoute, onOpenIdea: openIdeaRoute, onOpenStock: openStockRoute, onStart: startCustomRoute });
  return <div className="app"><a className="skip-link" href="#main-content">Skip to main content</a><Header onHome={goHomeRoute} /><div id="main-content" tabIndex={-1}>{routeContent}</div><Footer onHome={goHomeRoute} theme={theme} onThemeChange={setTheme} /></div>;
}

export default AppPremium;
